import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, uploadSubmission } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { useInscriptionOptions } from '../hooks/useInscriptionOptions';
import { EditorialTab } from '../components/booklet/EditorialTab';
import { ContributorsSection } from '../components/booklet/ContributorsSection';
import { ReviewerUploadControl } from '../components/ReviewerUploadControl';
import { ReviewerContentEditor } from '../components/ReviewerContentEditor';
import { InscriptionManagement } from '../components/booklet/InscriptionManagement';
import ReviewModal from '../components/booklet/ReviewModal';
import { ReviewCommentsView } from './weekend-booklet/review-comments/ReviewCommentsView';
import { ProductionLayoutTab } from '../components/booklet/ProductionLayoutTab';
import { VendorAccessTab } from '../components/booklet/VendorAccessTab';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// ── TypeScript Interfaces ─────────────────────────────────────

type SubmissionStatus = 'Draft' | 'Submitted' | 'UnderReview' | 'RevisionRequested' | 'Approved' | 'ReadyForLayout';
type SectionCategory = 'WelcomeMessage' | 'EventSchedule' | 'MemberProfiles' | 'SponsorAds' | 'MemorialTributes' | 'PhotoGallery' | 'CommitteeReports' | 'GeneralArticles' | 'Advertisers' | 'TileSpacePurchasers' | 'Templates';
type AdSize = 'FullPage' | 'HalfPage' | 'QuarterPage';
type PrintOrderStatus = 'Ordered' | 'Delivered';
type PaymentMethod = 'Cash' | 'Check' | 'CreditCard' | 'BankTransfer' | 'PayPal' | 'Zelle' | 'Other';

export interface BookletEdition {
  id: string;
  name: string;
  year: number | null;
  submissionDeadline: string;
  targetPageCount: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BookletSubmission {
  id: string;
  editionId: string;
  title: string;
  body: string | null;
  authorName: string;
  sectionCategory: SectionCategory;
  bookletSection: string | null;
  status: SubmissionStatus;
  sponsorId: string | null;
  adSize: AdSize | null;
  attachmentUrl: string | null;
  pageNumber: number | null;
  referenceId?: string | null;
  registrationId?: string | null;
  registration?: { id: string; user?: { name?: string; email?: string } } | null;
  inMemoryOf?: string | null;
  inscriptionId?: string | null;
  inscription?: { id: string; label: string } | null;
  createdAt: string;
  updatedAt: string;
  statusLogs?: SubmissionStatusLog[];
  reviewers?: ReviewAssignment[];
  comments?: ReviewComment[];
  versions?: SubmissionVersion[];
  spaceRevenue?: BookletSpaceRevenue;
}

export interface BookletSpaceRevenue {
  id: string;
  submissionId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  contactEmail: string | null;
  contactPhone: string | null;
  notes: string | null;
}

export interface SubmissionStatusLog {
  id: string;
  submissionId: string;
  fromStatus: SubmissionStatus | null;
  toStatus: SubmissionStatus;
  changedAt: string;
}

export interface ReviewAssignment {
  id: string;
  submissionId: string;
  memberId: string;
  assignedAt: string;
  reviewed: boolean;
  reviewedAt: string | null;
  reviewStatus?: string;
  member?: { id: string; firstName: string; lastName: string; userId?: string | null };
}

export interface MyReviewAssignment {
  assignmentId: string;
  memberId: string;
  reviewed: boolean;
  reviewedAt: string | null;
  reviewStatus: string;
  assignedAt: string;
  submission: {
    id: string;
    title: string;
    body: string | null;
    authorName: string;
    status: SubmissionStatus;
    bookletSection: string | null;
    attachmentUrl: string | null;
  };
}

export interface ReviewComment {
  id: string;
  submissionId: string;
  commenterName: string;
  commenterId?: string | null;
  text: string;
  isRichText?: boolean;
  commentType?: 'Review' | 'CommentsOnly';
  createdAt: string;
}

export interface SubmissionVersion {
  id: string;
  submissionId: string;
  versionNumber: number;
  title: string;
  body: string | null;
  savedAt: string;
}

export interface PrintOrder {
  id: string;
  editionId: string;
  quantity: number;
  printerVendor: string;
  totalCost: number | null;
  orderDate: string;
  expectedDeliveryDate: string | null;
  status: PrintOrderStatus;
  deliveredDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookletPricing {
  id: string;
  editionId: string;
  pricePerCopy: number;
  setAt: string;
}

export interface BookletSale {
  id: string;
  editionId: string;
  quantity: number;
  buyerName: string | null;
  paymentMethod: PaymentMethod;
  revenue: number;
  saleDate: string;
  createdAt: string;
}

export interface RevenueSummary {
  totalRevenue: number;
  transactionCount: number;
  breakdown: { paymentMethod: PaymentMethod; total: number; count: number }[];
}

export interface InventoryMetrics {
  totalPrinted: number;
  totalSold: number;
  remaining: number;
}

export interface ProductionProgress {
  statusCounts: { status: SubmissionStatus; count: number }[];
  totalSubmissions: number;
  uniqueAuthors: string[];
  deadlineCountdown: number | null;
  printOrderStatus: 'not_ordered' | 'ordered' | 'delivered';
}

export interface SpaceRevenueSummaryEntry {
  submissionId: string;
  authorName: string;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
}

export interface SpaceRevenueSummary {
  advertisers: { totalRevenue: number; count: number; entries: SpaceRevenueSummaryEntry[] };
  tileSpacePurchasers: { totalRevenue: number; count: number; entries: SpaceRevenueSummaryEntry[] };
  grandTotal: number;
}

// ── Tab Type ──────────────────────────────────────────────────

type TabKey = 'submissions' | 'articles' | 'memorialTiles' | 'adSpace' | 'templates' | 'layout' | 'production' | 'vendorAccess' | 'printSales' | 'dashboard' | 'editorial' | 'reviews';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'submissions', label: 'Submissions' },
  { key: 'articles', label: 'Articles' },
  { key: 'memorialTiles', label: 'Memorial Tiles' },
  { key: 'adSpace', label: 'Ad Space' },
  { key: 'templates', label: 'Templates' },
  { key: 'layout', label: 'Layout' },
  { key: 'production', label: 'Production' },
  { key: 'vendorAccess', label: 'Vendor Portal' },
  { key: 'printSales', label: 'Print & Sales' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'editorial', label: 'Editorial' },
  { key: 'reviews', label: 'Review Comments' },
];

// ── Articles View Constants & Helpers ──────────────────────────

export const BOOKLET_SECTIONS = [
  'Front Matter',
  'Feature Stories',
  'Heritage & History',
  'Faith & Culture',
  'People & Community',
  'Culture & Lifestyle',
  'KDU Global Impact',
  'Looking Forward',
  'Congratulatory Message',
  'Sponsors Message',
  'Booklet Templates',
] as const;

export const STATUS_COLORS: Record<SubmissionStatus, { bg: string; text: string }> = {
  Draft: { bg: '#f3f4f6', text: '#6b7280' },
  Submitted: { bg: '#dbeafe', text: '#1d4ed8' },
  UnderReview: { bg: '#fef3c7', text: '#d97706' },
  RevisionRequested: { bg: '#fee2e2', text: '#dc2626' },
  Approved: { bg: '#dcfce7', text: '#16a34a' },
  ReadyForLayout: { bg: '#f3e8ff', text: '#9333ea' },
};

// Column color scheme for Kanban board — each section gets a distinct tint
export const COLUMN_COLORS: Record<string, { headerBg: string; headerText: string; bodyBg: string; border: string }> = {
  'Unassigned':         { headerBg: '#6b7280', headerText: '#fff', bodyBg: '#f9fafb', border: '#d1d5db' },
  'Front Matter':       { headerBg: '#1a6b3c', headerText: '#fff', bodyBg: '#f0fdf4', border: '#bbf7d0' },
  'Feature Stories':    { headerBg: '#1d4ed8', headerText: '#fff', bodyBg: '#eff6ff', border: '#bfdbfe' },
  'Heritage & History': { headerBg: '#92400e', headerText: '#fff', bodyBg: '#fffbeb', border: '#fde68a' },
  'Faith & Culture':    { headerBg: '#7c3aed', headerText: '#fff', bodyBg: '#f5f3ff', border: '#ddd6fe' },
  'People & Community': { headerBg: '#be185d', headerText: '#fff', bodyBg: '#fdf2f8', border: '#fbcfe8' },
  'Culture & Lifestyle':{ headerBg: '#0891b2', headerText: '#fff', bodyBg: '#ecfeff', border: '#a5f3fc' },
  'KDU Global Impact':  { headerBg: '#c9a84c', headerText: '#1f2937', bodyBg: '#fefce8', border: '#fef08a' },
  'Looking Forward':    { headerBg: '#059669', headerText: '#fff', bodyBg: '#ecfdf5', border: '#a7f3d0' },
  'Booklet Templates':  { headerBg: '#4f46e5', headerText: '#fff', bodyBg: '#eef2ff', border: '#c7d2fe' },
};

// ── Memorial Tiles Constants ──────────────────────────────────

const TILES_PER_PAGE = 15;
const TILES_COLUMNS = 3;

// ── Ad Space Constants ────────────────────────────────────────

export const AD_SPACE_COLUMNS: string[] = ['Unassigned', 'FullPage', 'HalfPage', 'QuarterPage'];

export const AD_COLUMN_COLORS: Record<string, { headerBg: string; headerText: string; bodyBg: string; border: string }> = {
  'Unassigned':  { headerBg: '#6b7280', headerText: '#fff', bodyBg: '#f9fafb', border: '#d1d5db' },
  'FullPage':    { headerBg: '#7c3aed', headerText: '#fff', bodyBg: '#f5f3ff', border: '#ddd6fe' },
  'HalfPage':    { headerBg: '#0891b2', headerText: '#fff', bodyBg: '#ecfeff', border: '#a5f3fc' },
  'QuarterPage': { headerBg: '#c9a84c', headerText: '#1f2937', bodyBg: '#fefce8', border: '#fef08a' },
};

export const AD_SIZE_LABELS: Record<string, string> = {
  'Unassigned': 'Unassigned',
  'FullPage': 'Full Page',
  'HalfPage': 'Half Page',
  'QuarterPage': 'Quarter Page',
};

export function groupByAdSize(ads: BookletSubmission[]): Record<string, BookletSubmission[]> {
  const groups: Record<string, BookletSubmission[]> = {
    'Unassigned': [],
    'FullPage': [],
    'HalfPage': [],
    'QuarterPage': [],
  };

  ads.forEach(ad => {
    const size = ad.adSize || 'Unassigned';
    if (groups[size]) {
      groups[size].push(ad);
    } else {
      groups['Unassigned'].push(ad);
    }
  });

  return groups;
}

export function groupBySection(articles: BookletSubmission[]): Record<string, BookletSubmission[]> {
  const groups: Record<string, BookletSubmission[]> = { 'Unassigned': [] };
  BOOKLET_SECTIONS.forEach(section => { groups[section] = []; });

  articles.forEach(article => {
    const section = article.bookletSection || 'Unassigned';
    if (groups[section]) {
      groups[section].push(article);
    } else {
      groups['Unassigned'].push(article);
    }
  });

  return groups;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  tabBar: { display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' },
  tab: { padding: '0.75rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: '#666', borderBottom: '3px solid transparent', marginBottom: '-2px', transition: 'color 0.15s, border-color 0.15s' },
  tabActive: { padding: '0.75rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: '#1a6b3c', borderBottom: '3px solid #1a6b3c', marginBottom: '-2px' },
  tabContent: { minHeight: '200px' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
};

// ── Main Component ────────────────────────────────────────────

export function WeekendBookletPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  const { canEdit } = usePageAuthorization('weekend-booklet');
  const canEditContent = authUser?.role === 'Organizer' || authUser?.role === 'Admin';

  // Inscription options hook (for tile submissions)
  const { options: inscriptionOptions, loading: inscriptionLoading } = useInscriptionOptions();

  // Edition state
  const [activeEdition, setActiveEdition] = useState<BookletEdition | null>(null);
  const [editions, setEditions] = useState<BookletEdition[]>([]);

  // Submission state
  const [submissions, setSubmissions] = useState<BookletSubmission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<BookletSubmission | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<SectionCategory | null>(null);
  const [spaceRevenueSummary, setSpaceRevenueSummary] = useState<SpaceRevenueSummary | null>(null);

  // Print & Sales state
  const [printOrders, setPrintOrders] = useState<PrintOrder[]>([]);
  const [pricing, setPricing] = useState<BookletPricing[]>([]);
  const [sales, setSales] = useState<BookletSale[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary | null>(null);
  const [inventory, setInventory] = useState<InventoryMetrics | null>(null);

  // Dashboard state
  const [progress, setProgress] = useState<ProductionProgress | null>(null);

  // Submissions view mode: Delegates default to 'my-reviews', Organizer/Admin default to 'all'
  type SubmissionsViewMode = 'my-reviews' | 'all';
  const [submissionsViewMode, setSubmissionsViewMode] = useState<SubmissionsViewMode>(
    (searchParams.get('view') as SubmissionsViewMode) || (authUser?.role === 'Delegate' ? 'my-reviews' : 'all')
  );

  // My Reviews state
  const [myReviews, setMyReviews] = useState<MyReviewAssignment[]>([]);
  const [myReviewsLoading, setMyReviewsLoading] = useState(false);
  const [myReviewsError, setMyReviewsError] = useState('');

  // ReviewModal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalAssignment, setReviewModalAssignment] = useState<ReviewAssignment | null>(null);
  const [reviewModalSubmission, setReviewModalSubmission] = useState<BookletSubmission | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<TabKey>(
    (searchParams.get('tab') as TabKey) || 'submissions'
  );
  const [articlesViewMode, setArticlesViewMode] = useState<'kanban' | 'table'>('kanban');
  const [tilesPage, setTilesPage] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<BookletSubmission | null>(null);

  // Edition management state
  const [showCreateEdition, setShowCreateEdition] = useState(false);
  const [editingEdition, setEditingEdition] = useState(false);
  const [editionForm, setEditionForm] = useState({ name: '', year: '', submissionDeadline: '', targetPageCount: '' });
  const [editionSubmitting, setEditionSubmitting] = useState(false);

  // Submission form state
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [submissionForm, setSubmissionForm] = useState({
    title: '',
    body: '',
    authorName: '',
    sectionCategory: '' as SectionCategory | '',
    bookletSection: '',
    sponsorId: '',
    adSize: '' as AdSize | '',
    attachmentUrl: '',
    referenceId: '',
  });
  const [submissionSubmitting, setSubmissionSubmitting] = useState(false);

  // Tile fields state (In Memory Of + Inscription)
  const [inMemoryOf, setInMemoryOf] = useState('');
  const [inscriptionId, setInscriptionId] = useState('');

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);

  // Revenue form state (for Advertisers / TileSpacePurchasers)
  const [revenueForm, setRevenueForm] = useState({
    amount: '',
    paymentMethod: '' as PaymentMethod | '',
    paymentDate: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  });
  const [revenueFormError, setRevenueFormError] = useState('');

  // Committee members state (for reviewer dropdown and editorial tab access)
  const [committeeMembers, setCommitteeMembers] = useState<{ id: string; firstName: string; lastName: string; userId?: string | null }[]>([]);

  // Submission detail expansion state
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [statusChangeTarget, setStatusChangeTarget] = useState<SubmissionStatus | ''>('');
  const [statusChangeError, setStatusChangeError] = useState('');
  const [statusChanging, setStatusChanging] = useState(false);
  const [reviewerInput, setReviewerInput] = useState('');
  const [assigningReviewer, setAssigningReviewer] = useState(false);
  const [commentForm, setCommentForm] = useState({ commenterName: '', text: '' });
  const [addingComment, setAddingComment] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  // Submission edit state
  const [editingSubmission, setEditingSubmission] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', body: '', bookletSection: '', referenceId: '', sectionCategory: '' as SectionCategory | '', authorName: '', inMemoryOf: '', inscriptionId: '' });
  const [editRevenueForm, setEditRevenueForm] = useState({ amount: '', paymentMethod: '' as PaymentMethod | '', paymentDate: '', contactEmail: '', contactPhone: '', notes: '' });
  const [editRegistrationId, setEditRegistrationId] = useState('');
  const [editRegistrationSearch, setEditRegistrationSearch] = useState('');
  const [registrationResults, setRegistrationResults] = useState<{ id: string; name: string; email: string }[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  // Edit file upload state
  const [editSelectedFile, setEditSelectedFile] = useState<File | null>(null);
  const [editFileError, setEditFileError] = useState('');

  // Layout state
  const [layoutAllocated, setLayoutAllocated] = useState<BookletSubmission[]>([]);
  const [layoutUnallocated, setLayoutUnallocated] = useState<BookletSubmission[]>([]);
  const [pageInputs, setPageInputs] = useState<Record<string, string>>({});
  const [layoutAssigning, setLayoutAssigning] = useState<string | null>(null);

  // Print order form state
  const [showPrintOrderForm, setShowPrintOrderForm] = useState(false);
  const [printOrderForm, setPrintOrderForm] = useState({
    quantity: '',
    printerVendor: '',
    totalCost: '',
    orderDate: '',
    expectedDeliveryDate: '',
  });
  const [printOrderSubmitting, setPrintOrderSubmitting] = useState(false);

  // Pricing form state
  const [priceInput, setPriceInput] = useState('');
  const [pricingSubmitting, setPricingSubmitting] = useState(false);

  // Sale form state
  const [saleForm, setSaleForm] = useState({ quantity: '', buyerName: '', paymentMethod: '' as PaymentMethod | '' });
  const [saleSubmitting, setSaleSubmitting] = useState(false);
  const [saleWarning, setSaleWarning] = useState('');

  // Notify All Reviewers modal state
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState(false);

  // Inscription Options collapsible panel state (Admin only)
  const [inscriptionPanelOpen, setInscriptionPanelOpen] = useState(false);

  // Suppress unused warnings for state used by later tasks
  void editions;
  void TILES_COLUMNS;

  // Derived flag: is the submission category TileSpacePurchasers?
  const isTileCategory = submissionForm.sectionCategory === 'TileSpacePurchasers';

  // Clear tile fields when category changes away from TileSpacePurchasers
  useEffect(() => {
    if (!isTileCategory) {
      setInMemoryOf('');
      setInscriptionId('');
    }
  }, [isTileCategory]);

  // ── Data Fetching ─────────────────────────────────────────────

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      // Fetch active edition first (may not exist yet)
      const edition = await apiClient<BookletEdition>('/weekend-booklet/editions/active').catch(() => null);
      setActiveEdition(edition);

      if (edition) {
        // Fetch all dependent data in parallel
        const [
          submissionsData,
          progressData,
          revenueData,
          inventoryData,
          salesData,
          pricingData,
          editionDetail,
          spaceRevenueData,
        ] = await Promise.all([
          apiClient<BookletSubmission[]>(`/weekend-booklet/submissions?editionId=${edition.id}`),
          apiClient<ProductionProgress>(`/weekend-booklet/editions/${edition.id}/progress`).catch(() => null),
          apiClient<RevenueSummary>(`/weekend-booklet/editions/${edition.id}/revenue`).catch(() => null),
          apiClient<InventoryMetrics>(`/weekend-booklet/editions/${edition.id}/inventory`).catch(() => null),
          apiClient<BookletSale[]>(`/weekend-booklet/editions/${edition.id}/sales`).catch(() => []),
          apiClient<BookletPricing[]>(`/weekend-booklet/editions/${edition.id}/pricing`).catch(() => []),
          apiClient<BookletEdition & { printOrders?: PrintOrder[] }>(`/weekend-booklet/editions/${edition.id}`).catch(() => null),
          apiClient<SpaceRevenueSummary>(`/weekend-booklet/editions/${edition.id}/space-revenue`).catch(() => null),
        ]);

        setSubmissions(submissionsData);
        setProgress(progressData);
        setRevenueSummary(revenueData);
        setInventory(inventoryData);
        setSales(salesData as BookletSale[]);
        setPricing(pricingData as BookletPricing[]);
        setPrintOrders(editionDetail?.printOrders || []);
        setSpaceRevenueSummary(spaceRevenueData);
      }

      // Also fetch all editions for the edition list
      const allEditions = await apiClient<BookletEdition[]>('/weekend-booklet/editions');
      setEditions(allEditions);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load booklet data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    fetchCommitteeMembers();
  }, []);

  // ── Layout Data Fetching ──────────────────────────────────────

  async function fetchLayoutData() {
    if (!activeEdition) return;
    try {
      const data = await apiClient<{ allocated: BookletSubmission[]; unallocated: BookletSubmission[] }>(
        `/weekend-booklet/editions/${activeEdition.id}/layout`
      );
      setLayoutAllocated(data.allocated);
      setLayoutUnallocated(data.unallocated);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load layout data.');
    }
  }

  useEffect(() => {
    if (activeTab === 'layout' && activeEdition) {
      fetchLayoutData();
    }
  }, [activeTab, activeEdition]);

  // ── My Reviews Data Fetching ──────────────────────────────────

  async function fetchMyReviews() {
    setMyReviewsLoading(true);
    setMyReviewsError('');
    try {
      const data = await apiClient<MyReviewAssignment[]>('/weekend-booklet/my-reviews');
      setMyReviews(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setMyReviewsError(apiErr.message || 'Failed to load your reviews.');
    } finally {
      setMyReviewsLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'submissions') {
      // Always fetch my reviews when on submissions tab (needed for assignment indicators in both views)
      fetchMyReviews();
    }
  }, [activeTab]);

  // ── ReviewModal Handlers ────────────────────────────────────

  function openReviewModal(submission: BookletSubmission, assignment: ReviewAssignment | null) {
    setReviewModalSubmission(submission);
    setReviewModalAssignment(assignment);
    setReviewModalOpen(true);
  }

  function handleReviewModalClose() {
    setReviewModalOpen(false);
    setReviewModalSubmission(null);
    setReviewModalAssignment(null);
  }

  const handleReviewCommentAdded = useCallback((_comment: ReviewComment) => {
    // Refresh submissions data after a comment is added
    if (activeEdition) {
      let url = `/weekend-booklet/submissions?editionId=${activeEdition.id}`;
      if (categoryFilter) url += `&sectionCategory=${categoryFilter}`;
      apiClient<BookletSubmission[]>(url).then(setSubmissions).catch(() => {});
    }
  }, [activeEdition, categoryFilter]);

  const handleReviewStatusChanged = useCallback((_newStatus: string) => {
    // Refresh myReviews data after a status change so badges update
    fetchMyReviews();
    // Also refresh submissions list
    if (activeEdition) {
      let url = `/weekend-booklet/submissions?editionId=${activeEdition.id}`;
      if (categoryFilter) url += `&sectionCategory=${categoryFilter}`;
      apiClient<BookletSubmission[]>(url).then(setSubmissions).catch(() => {});
    }
  }, [activeEdition, categoryFilter]);

  function handleMyReviewClick(review: MyReviewAssignment) {
    // Build a BookletSubmission-compatible object from MyReviewAssignment
    const sub: BookletSubmission = {
      id: review.submission.id,
      editionId: activeEdition?.id || '',
      title: review.submission.title,
      body: review.submission.body,
      authorName: review.submission.authorName,
      sectionCategory: 'GeneralArticles' as SectionCategory,
      bookletSection: review.submission.bookletSection,
      status: review.submission.status,
      sponsorId: null,
      adSize: null,
      attachmentUrl: review.submission.attachmentUrl,
      pageNumber: null,
      createdAt: '',
      updatedAt: '',
    };
    // Build a ReviewAssignment from the MyReviewAssignment data
    const assignment: ReviewAssignment = {
      id: review.assignmentId,
      submissionId: review.submission.id,
      memberId: review.memberId,
      assignedAt: review.assignedAt,
      reviewed: review.reviewed,
      reviewedAt: review.reviewedAt,
      reviewStatus: review.reviewStatus,
    };
    openReviewModal(sub, assignment);
  }

  function handleAllSubmissionsClick(sub: BookletSubmission) {
    // Check if this submission is assigned to the current user
    const myReview = myReviews.find((r) => r.submission.id === sub.id);
    if (myReview) {
      // Build ReviewAssignment from myReview data
      const assignment: ReviewAssignment = {
        id: myReview.assignmentId,
        submissionId: myReview.submission.id,
        memberId: myReview.memberId,
        assignedAt: myReview.assignedAt,
        reviewed: myReview.reviewed,
        reviewedAt: myReview.reviewedAt,
        reviewStatus: myReview.reviewStatus,
      };
      openReviewModal(sub, assignment);
    } else {
      // Not assigned — pass null for assignment
      openReviewModal(sub, null);
    }
  }

  // ── Tab Content Renderers ───────────────────────────────────

  // ── Edition Helpers ───────────────────────────────────────────

  function getDeadlineInfo(deadline: string): { text: string; color: string } {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    // Reset time to compare dates only
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const deadlineDay = new Date(deadlineDate.getFullYear(), deadlineDate.getMonth(), deadlineDate.getDate());
    const diffMs = deadlineDay.getTime() - nowDay.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: 'Deadline Passed', color: '#d32f2f' };
    }
    if (diffDays === 0) {
      return { text: 'Due Today', color: '#d32f2f' };
    }
    if (diffDays <= 7) {
      return { text: `${diffDays} day${diffDays === 1 ? '' : 's'} remaining`, color: '#e65100' };
    }
    return { text: `${diffDays} days remaining`, color: '#1a6b3c' };
  }

  function resetEditionForm() {
    setEditionForm({ name: '', year: '', submissionDeadline: '', targetPageCount: '' });
  }

  function startEditEdition() {
    if (!activeEdition) return;
    setEditionForm({
      name: activeEdition.name,
      year: activeEdition.year?.toString() || '',
      submissionDeadline: activeEdition.submissionDeadline ? activeEdition.submissionDeadline.split('T')[0] : '',
      targetPageCount: activeEdition.targetPageCount?.toString() || '',
    });
    setEditingEdition(true);
    setShowCreateEdition(false);
  }

  async function handleCreateEdition(e: React.FormEvent) {
    e.preventDefault();
    if (!editionForm.name || !editionForm.submissionDeadline) return;
    setEditionSubmitting(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        name: editionForm.name,
        submissionDeadline: new Date(editionForm.submissionDeadline).toISOString(),
      };
      if (editionForm.year) payload.year = parseInt(editionForm.year, 10);
      if (editionForm.targetPageCount) payload.targetPageCount = parseInt(editionForm.targetPageCount, 10);

      const created = await apiClient<BookletEdition>('/weekend-booklet/editions', { method: 'POST', body: payload });
      // Automatically activate the new edition
      await apiClient(`/weekend-booklet/editions/${created.id}/activate`, { method: 'PATCH' });
      resetEditionForm();
      setShowCreateEdition(false);
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create edition.');
    } finally {
      setEditionSubmitting(false);
    }
  }

  async function handleEditEdition(e: React.FormEvent) {
    e.preventDefault();
    if (!activeEdition || !editionForm.name || !editionForm.submissionDeadline) return;
    setEditionSubmitting(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        name: editionForm.name,
        submissionDeadline: new Date(editionForm.submissionDeadline).toISOString(),
      };
      if (editionForm.year) payload.year = parseInt(editionForm.year, 10);
      else payload.year = null;
      if (editionForm.targetPageCount) payload.targetPageCount = parseInt(editionForm.targetPageCount, 10);
      else payload.targetPageCount = null;

      await apiClient(`/weekend-booklet/editions/${activeEdition.id}`, { method: 'PATCH', body: payload });
      resetEditionForm();
      setEditingEdition(false);
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update edition.');
    } finally {
      setEditionSubmitting(false);
    }
  }

  // ── Submission Helpers ──────────────────────────────────────

  const SECTION_CATEGORIES: SectionCategory[] = [
    'WelcomeMessage', 'EventSchedule', 'MemberProfiles', 'SponsorAds',
    'MemorialTributes', 'PhotoGallery', 'CommitteeReports', 'GeneralArticles',
    'Advertisers', 'TileSpacePurchasers', 'Templates',
  ];

  const AD_SIZES: AdSize[] = ['FullPage', 'HalfPage', 'QuarterPage'];

  const PAYMENT_METHODS: PaymentMethod[] = ['Cash', 'Check', 'CreditCard', 'BankTransfer', 'PayPal', 'Zelle', 'Other'];

  const STATUS_BADGE_COLORS: Record<SubmissionStatus, string> = {
    Draft: '#6b7280',
    Submitted: '#2563eb',
    UnderReview: '#d97706',
    RevisionRequested: '#dc2626',
    Approved: '#16a34a',
    ReadyForLayout: '#7c3aed',
  };

  // Review status badge colors (per design: Pending=gray, Reviewed=blue, Changes_Requested=orange, Approved=green)
  const REVIEW_STATUS_BADGES: Record<string, { bg: string; text: string; label: string }> = {
    Pending: { bg: '#f3f4f6', text: '#6b7280', label: 'Pending' },
    Reviewed: { bg: '#dbeafe', text: '#1d4ed8', label: 'Reviewed' },
    Changes_Requested: { bg: '#fff7ed', text: '#c2410c', label: 'Changes Requested' },
    Approved: { bg: '#dcfce7', text: '#16a34a', label: 'Approved' },
  };

  const VALID_TRANSITIONS: Record<SubmissionStatus, SubmissionStatus[]> = {
    Draft: ['Submitted'],
    Submitted: ['UnderReview'],
    UnderReview: ['RevisionRequested', 'Approved'],
    RevisionRequested: ['Submitted'],
    Approved: ['ReadyForLayout'],
    ReadyForLayout: [],
  };

  // ── Folder Navigation Helpers ─────────────────────────────────

  async function handleFolderClick(category: SectionCategory | null) {
    if (!activeEdition) return;
    setCategoryFilter(category);
    setSelectedSubmission(null);
    try {
      let url = `/weekend-booklet/submissions?editionId=${activeEdition.id}`;
      if (category) {
        url += `&sectionCategory=${category}`;
      }
      const updatedSubmissions = await apiClient<BookletSubmission[]>(url);
      setSubmissions(updatedSubmissions);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to filter submissions.');
    }
  }

  // ── Committee Members Fetching ─────────────────────────────────

  let registrationSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  async function searchRegistrations(query: string) {
    if (registrationSearchTimeout) clearTimeout(registrationSearchTimeout);
    if (!query || query.length < 2) {
      setRegistrationResults([]);
      return;
    }
    registrationSearchTimeout = setTimeout(async () => {
      try {
        const regs = await apiClient<Array<{ id: string; fullName: string; email: string }>>(
          '/legacy-ball/registrations'
        );
        const filtered = regs
          .filter((r) => {
            const name = (r.fullName || '').toLowerCase();
            const email = (r.email || '').toLowerCase();
            const q = query.toLowerCase();
            return name.includes(q) || email.includes(q);
          })
          .slice(0, 8)
          .map((r) => ({ id: r.id, name: r.fullName || '', email: r.email || '' }));
        setRegistrationResults(filtered);
      } catch {
        setRegistrationResults([]);
      }
    }, 300);
  }

  async function fetchCommitteeMembers() {
    const members = await apiClient<{ id: string; firstName: string; lastName: string; userId?: string | null }[]>(
      '/weekend-booklet/committee-members'
    );
    setCommitteeMembers(members);
  }

  function getAvailableReviewers() {
    if (!selectedSubmission?.reviewers) return committeeMembers;
    const assignedIds = new Set(selectedSubmission.reviewers.map((r) => r.memberId));
    return committeeMembers.filter((m) => !assignedIds.has(m.id));
  }

  // ── Submission Detail Helpers ─────────────────────────────────

  async function handleSubmissionClick(sub: BookletSubmission) {
    if (selectedSubmission?.id === sub.id) {
      // Collapse
      setSelectedSubmission(null);
      setDetailError('');
      setStatusChangeTarget('');
      setStatusChangeError('');
      setShowVersions(false);
      return;
    }
    // Expand: fetch full detail
    setDetailLoading(true);
    setDetailError('');
    setStatusChangeTarget('');
    setStatusChangeError('');
    setShowVersions(false);
    setCommentForm({ commenterName: authUser?.fullName || authUser?.email || '', text: '' });
    setReviewerInput('');
    setEditingSubmission(false);
    try {
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${sub.id}`);
      setSelectedSubmission(detail);
      fetchCommitteeMembers();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setDetailError(apiErr.message || 'Failed to load submission details.');
      setSelectedSubmission(sub);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleStatusChange() {
    if (!selectedSubmission || !statusChangeTarget) return;
    setStatusChanging(true);
    setStatusChangeError('');
    try {
      await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}`, {
        method: 'PATCH',
        body: { status: statusChangeTarget },
      });
      // Refresh detail
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${selectedSubmission.id}`);
      setSelectedSubmission(detail);
      setStatusChangeTarget('');
      // Also refresh the submissions list
      if (activeEdition) {
        let url = `/weekend-booklet/submissions?editionId=${activeEdition.id}`;
        if (categoryFilter) url += `&sectionCategory=${categoryFilter}`;
        const updatedSubmissions = await apiClient<BookletSubmission[]>(url);
        setSubmissions(updatedSubmissions);
      }
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setStatusChangeError(apiErr.message || 'Invalid status transition.');
    } finally {
      setStatusChanging(false);
    }
  }

  async function handleAssignReviewer() {
    if (!selectedSubmission || !reviewerInput.trim()) return;
    setAssigningReviewer(true);
    try {
      await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}/reviewers`, {
        method: 'POST',
        body: { memberId: reviewerInput.trim() },
      });
      // Refresh detail
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${selectedSubmission.id}`);
      setSelectedSubmission(detail);
      setReviewerInput('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setDetailError(apiErr.message || 'Failed to assign reviewer.');
    } finally {
      setAssigningReviewer(false);
    }
  }

  async function handleRemoveReviewer(memberId: string) {
    if (!selectedSubmission) return;
    try {
      await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}/reviewers/${memberId}`, {
        method: 'DELETE',
      });
      // Refresh detail
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${selectedSubmission.id}`);
      setSelectedSubmission(detail);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setDetailError(apiErr.message || 'Failed to remove reviewer.');
    }
  }

  async function handleToggleReviewed(memberId: string) {
    if (!selectedSubmission) return;
    try {
      await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}/reviewers/${memberId}/toggle-reviewed`, {
        method: 'PATCH',
      });
      // Refresh detail
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${selectedSubmission.id}`);
      setSelectedSubmission(detail);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setDetailError(apiErr.message || 'Failed to toggle review status.');
    }
  }

  async function handleAddComment(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedSubmission || !commentForm.text.trim() || !commentForm.commenterName.trim()) return;
    setAddingComment(true);
    try {
      await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}/comments`, {
        method: 'POST',
        body: { commenterName: commentForm.commenterName.trim(), text: commentForm.text.trim() },
      });
      // Refresh detail
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${selectedSubmission.id}`);
      setSelectedSubmission(detail);
      setCommentForm({ commenterName: authUser?.fullName || authUser?.email || '', text: '' });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setDetailError(apiErr.message || 'Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  }

  // ── File Upload Validation ─────────────────────────────────────
  const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.docx', '.png', '.jpg', '.jpeg'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  function validateFile(file: File): string {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
      return 'Invalid file type. Allowed types: PDF, DOCX, PNG, JPG.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 10MB.';
    }
    return '';
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file) {
      const error = validateFile(file);
      setFileError(error);
    } else {
      setFileError('');
    }
  }

  function handleEditFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    setEditSelectedFile(file);
    if (file) {
      const error = validateFile(file);
      setEditFileError(error);
    } else {
      setEditFileError('');
    }
  }

  async function handleSaveEdit() {
    if (!selectedSubmission) return;

    // Validate selected file before saving
    if (editSelectedFile) {
      const error = validateFile(editSelectedFile);
      if (error) {
        setEditFileError(error);
        return;
      }
    }

    setEditSaving(true);
    setUploading(true);
    setDetailError('');
    try {
      const fields: Record<string, string> = {};
      // Always send these fields for TileSpacePurchasers/Advertisers to ensure they persist
      if (editForm.sectionCategory === 'TileSpacePurchasers' || editForm.sectionCategory === 'Advertisers') {
        fields.title = editForm.title;
        fields.body = editForm.body;
        fields.authorName = editForm.authorName;
        if (editForm.referenceId) fields.referenceId = editForm.referenceId;
        if (editForm.sectionCategory) fields.sectionCategory = editForm.sectionCategory;
        if (editRegistrationId) fields.registrationId = editRegistrationId;
        else if (selectedSubmission.registrationId && !editRegistrationId) fields.registrationId = '';
        // Tile-specific fields
        if (editForm.sectionCategory === 'TileSpacePurchasers') {
          fields.inMemoryOf = editForm.inMemoryOf || '';
          if (editForm.inscriptionId) fields.inscriptionId = editForm.inscriptionId;
        }
      } else {
        if (editForm.title !== selectedSubmission.title) fields.title = editForm.title;
        if (editForm.body !== (selectedSubmission.body || '')) fields.body = editForm.body;
        if (editForm.sectionCategory && editForm.sectionCategory !== selectedSubmission.sectionCategory) {
          fields.sectionCategory = editForm.sectionCategory;
        }
        if (editForm.sectionCategory === 'GeneralArticles' && editForm.bookletSection !== (selectedSubmission.bookletSection || '')) {
          fields.bookletSection = editForm.bookletSection || '';
        }
        if (editForm.referenceId !== (selectedSubmission.referenceId || '')) {
          fields.referenceId = editForm.referenceId || '';
        }
        if (editForm.authorName !== (selectedSubmission.authorName || '')) {
          fields.authorName = editForm.authorName;
        }
      }
      if (Object.keys(fields).length > 0 || editSelectedFile) {
        await uploadSubmission(
          `/weekend-booklet/submissions/${selectedSubmission.id}/upload`,
          'PATCH',
          fields,
          editSelectedFile || undefined,
        );
      }
      // Save revenue if category is TileSpacePurchasers or Advertisers and amount is provided
      if ((editForm.sectionCategory === 'TileSpacePurchasers' || editForm.sectionCategory === 'Advertisers') && editRevenueForm.amount) {
        const revenuePayload: Record<string, unknown> = {
          amount: parseFloat(editRevenueForm.amount),
        };
        if (editRevenueForm.paymentMethod) revenuePayload.paymentMethod = editRevenueForm.paymentMethod;
        if (editRevenueForm.paymentDate) revenuePayload.paymentDate = new Date(editRevenueForm.paymentDate).toISOString();
        if (editRevenueForm.contactEmail) revenuePayload.contactEmail = editRevenueForm.contactEmail;
        if (editRevenueForm.contactPhone) revenuePayload.contactPhone = editRevenueForm.contactPhone;
        if (editRevenueForm.notes) revenuePayload.notes = editRevenueForm.notes;

        // Try PATCH first (update existing), fall back to POST (create new) if 404
        try {
          await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}/revenue`, { method: 'PATCH', body: revenuePayload });
        } catch (revenueErr: unknown) {
          const revErrObj = revenueErr as { statusCode?: number };
          if (revErrObj?.statusCode === 404) {
            await apiClient(`/weekend-booklet/submissions/${selectedSubmission.id}/revenue`, { method: 'POST', body: revenuePayload });
          }
          // If it's another error, ignore it — the main submission was already saved
        }
      }
      // Refresh detail
      const detail = await apiClient<BookletSubmission>(`/weekend-booklet/submissions/${selectedSubmission.id}`);
      setSelectedSubmission(detail);
      setEditingSubmission(false);
      setEditSelectedFile(null);
      setEditFileError('');
      // Refresh submissions list
      if (activeEdition) {
        let url = `/weekend-booklet/submissions?editionId=${activeEdition.id}`;
        if (categoryFilter) url += `&sectionCategory=${categoryFilter}`;
        const updatedSubmissions = await apiClient<BookletSubmission[]>(url);
        setSubmissions(updatedSubmissions);
      }
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setDetailError(apiErr.message || 'Failed to save changes.');
    } finally {
      setEditSaving(false);
      setUploading(false);
    }
  }

  async function handleSubmissionSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeEdition || !submissionForm.title || !submissionForm.authorName || !submissionForm.sectionCategory) return;

    // Validate selected file before submission
    if (selectedFile) {
      const error = validateFile(selectedFile);
      if (error) {
        setFileError(error);
        return;
      }
    }

    const isRevenueCategory = submissionForm.sectionCategory === 'Advertisers' || submissionForm.sectionCategory === 'TileSpacePurchasers';

    // Validate revenue fields if applicable
    if (isRevenueCategory) {
      const amount = parseFloat(revenueForm.amount);
      if (!revenueForm.amount || isNaN(amount) || amount <= 0) {
        setRevenueFormError('Amount must be a positive number.');
        return;
      }
      if (!revenueForm.paymentMethod) {
        setRevenueFormError('Payment method is required.');
        return;
      }
      if (!revenueForm.paymentDate) {
        setRevenueFormError('Payment date is required.');
        return;
      }
      setRevenueFormError('');
    }

    setSubmissionSubmitting(true);
    setUploading(true);
    setError('');
    try {
      const fields: Record<string, string> = {
        title: submissionForm.title,
        authorName: submissionForm.authorName,
        sectionCategory: submissionForm.sectionCategory,
        editionId: activeEdition.id,
      };
      if (submissionForm.body) fields.body = submissionForm.body;
      if (submissionForm.attachmentUrl) fields.attachmentUrl = submissionForm.attachmentUrl;
      if (submissionForm.referenceId) fields.referenceId = submissionForm.referenceId;
      if (submissionForm.sectionCategory === 'GeneralArticles' && submissionForm.bookletSection) {
        fields.bookletSection = submissionForm.bookletSection;
      }
      if (submissionForm.sectionCategory === 'SponsorAds') {
        if (submissionForm.sponsorId) fields.sponsorId = submissionForm.sponsorId;
        if (submissionForm.adSize) fields.adSize = submissionForm.adSize;
      }
      if (submissionForm.sectionCategory === 'TileSpacePurchasers') {
        if (submissionForm.adSize) fields.adSize = submissionForm.adSize;
        if (submissionForm.sponsorId) fields.sponsorId = submissionForm.sponsorId;
        if (inMemoryOf.trim()) fields.inMemoryOf = inMemoryOf.trim();
        if (inscriptionId) fields.inscriptionId = inscriptionId;
      }
      const created = await uploadSubmission<BookletSubmission>(
        '/weekend-booklet/submissions/upload',
        'POST',
        fields,
        selectedFile || undefined,
      );

      // If revenue category, create revenue record
      if (isRevenueCategory) {
        const revenuePayload: Record<string, unknown> = {
          amount: parseFloat(revenueForm.amount),
          paymentMethod: revenueForm.paymentMethod,
          paymentDate: new Date(revenueForm.paymentDate).toISOString(),
        };
        if (revenueForm.contactEmail) revenuePayload.contactEmail = revenueForm.contactEmail;
        if (revenueForm.contactPhone) revenuePayload.contactPhone = revenueForm.contactPhone;
        if (revenueForm.notes) revenuePayload.notes = revenueForm.notes;

        await apiClient(`/weekend-booklet/submissions/${created.id}/revenue`, { method: 'POST', body: revenuePayload });
      }

      // Reset form and refresh
      setSubmissionForm({ title: '', body: '', authorName: '', sectionCategory: '', bookletSection: '', sponsorId: '', adSize: '', attachmentUrl: '', referenceId: '' });
      setInMemoryOf('');
      setInscriptionId('');
      setSelectedFile(null);
      setFileError('');
      setRevenueForm({ amount: '', paymentMethod: '', paymentDate: '', contactEmail: '', contactPhone: '', notes: '' });
      setRevenueFormError('');
      setShowSubmissionForm(false);
      // Refresh submissions
      let refreshUrl = `/weekend-booklet/submissions?editionId=${activeEdition.id}`;
      if (categoryFilter) refreshUrl += `&sectionCategory=${categoryFilter}`;
      const updatedSubmissions = await apiClient<BookletSubmission[]>(refreshUrl);
      setSubmissions(updatedSubmissions);
      // Also refresh space revenue summary
      const updatedSpaceRevenue = await apiClient<SpaceRevenueSummary>(`/weekend-booklet/editions/${activeEdition.id}/space-revenue`).catch(() => null);
      setSpaceRevenueSummary(updatedSpaceRevenue);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create submission.');
    } finally {
      setSubmissionSubmitting(false);
      setUploading(false);
    }
  }

  // ── Submissions Tab Renderer ────────────────────────────────

  function renderSubmissionsTab() {
    const deadlineInfo = activeEdition ? getDeadlineInfo(activeEdition.submissionDeadline) : null;

    return (
      <div>
        {/* Active Edition Overview */}
        {activeEdition ? (
          <div style={{ ...styles.section, borderLeft: '4px solid #1a6b3c' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ ...styles.sectionTitle, margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
                {activeEdition.name}
              </h3>
              {canEdit && (
              <button
                onClick={startEditEdition}
                style={{
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.8rem',
                  backgroundColor: '#c9a84c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Edit Edition
              </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              {/* Deadline Countdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deadline</span>
                <span style={{ fontSize: '1rem', fontWeight: 600, color: deadlineInfo?.color }}>
                  {deadlineInfo?.text}
                </span>
              </div>
              {/* Target Page Count */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Target Pages</span>
                <span style={{ fontSize: '1rem', fontWeight: 600, color: '#0f2d1f' }}>
                  {activeEdition.targetPageCount ?? '—'}
                </span>
              </div>
              {/* Submission Count */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Submissions</span>
                <span style={{ fontSize: '1rem', fontWeight: 600, color: '#0f2d1f' }}>
                  {submissions.length}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ ...styles.section, textAlign: 'center' as const }}>
            <p style={{ color: '#666', margin: '0 0 1rem 0' }}>No active edition. Create one to get started.</p>
          </div>
        )}

        {/* Edit Edition Form */}
        {editingEdition && activeEdition && (
          <div style={{ ...styles.section, borderLeft: '4px solid #c9a84c' }}>
            <h3 style={{ ...styles.sectionTitle, fontSize: '1rem' }}>Edit Edition</h3>
            <form onSubmit={handleEditEdition} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>
                    Name <span style={{ color: '#d32f2f' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={editionForm.name}
                    onChange={(e) => setEditionForm({ ...editionForm, name: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>Year</label>
                  <input
                    type="number"
                    value={editionForm.year}
                    onChange={(e) => setEditionForm({ ...editionForm, year: e.target.value })}
                    placeholder="e.g. 2026"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>
                    Submission Deadline <span style={{ color: '#d32f2f' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={editionForm.submissionDeadline}
                    onChange={(e) => setEditionForm({ ...editionForm, submissionDeadline: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>Target Page Count</label>
                  <input
                    type="number"
                    value={editionForm.targetPageCount}
                    onChange={(e) => setEditionForm({ ...editionForm, targetPageCount: e.target.value })}
                    min="1"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={editionSubmitting}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#1a6b3c',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: editionSubmitting ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    opacity: editionSubmitting ? 0.6 : 1,
                  }}
                >
                  {editionSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingEdition(false); resetEditionForm(); }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'transparent',
                    color: '#666',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Create Edition Toggle & Form */}
        {!editingEdition && (
          <div style={{ marginBottom: '1.5rem' }}>
            {!showCreateEdition ? (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                {canEdit && <button
                  onClick={() => { setShowCreateEdition(true); resetEditionForm(); }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#1a6b3c',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                  }}
                >
                  + Create Edition
                </button>}
                {authUser?.role === 'Admin' && (
                  <button
                    onClick={() => setInscriptionPanelOpen(!inscriptionPanelOpen)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: inscriptionPanelOpen ? '#e5e7eb' : '#f9fafb',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    ⚙️ Inscription Options
                  </button>
                )}
              </div>
            ) : (
              <div style={{ ...styles.section, borderLeft: '4px solid #1a6b3c' }}>
                <h3 style={{ ...styles.sectionTitle, fontSize: '1rem' }}>Create New Edition</h3>
                <form onSubmit={handleCreateEdition} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>
                        Name <span style={{ color: '#d32f2f' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={editionForm.name}
                        onChange={(e) => setEditionForm({ ...editionForm, name: e.target.value })}
                        required
                        placeholder="e.g. Summer 2026 Edition"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>Year</label>
                      <input
                        type="number"
                        value={editionForm.year}
                        onChange={(e) => setEditionForm({ ...editionForm, year: e.target.value })}
                        placeholder="e.g. 2026"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>
                        Submission Deadline <span style={{ color: '#d32f2f' }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={editionForm.submissionDeadline}
                        onChange={(e) => setEditionForm({ ...editionForm, submissionDeadline: e.target.value })}
                        required
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#333', marginBottom: '0.25rem' }}>Target Page Count</label>
                      <input
                        type="number"
                        value={editionForm.targetPageCount}
                        onChange={(e) => setEditionForm({ ...editionForm, targetPageCount: e.target.value })}
                        min="1"
                        placeholder="e.g. 48"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      type="submit"
                      disabled={editionSubmitting}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#1a6b3c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: editionSubmitting ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        opacity: editionSubmitting ? 0.6 : 1,
                      }}
                    >
                      {editionSubmitting ? 'Creating...' : 'Create Edition'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCreateEdition(false); resetEditionForm(); }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'transparent',
                        color: '#666',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Inscription Options — shown when admin clicks the button */}
        {authUser?.role === 'Admin' && inscriptionPanelOpen && (
          <div style={{ marginBottom: '1.5rem' }}>
            <InscriptionManagement />
          </div>
        )}

        {/* Category Tabs */}
        {activeEdition && (
          <div style={{ ...styles.section, padding: '0.75rem 1.5rem' }}>
            <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e5e7eb', overflowX: 'auto' }}>
              {[
                { key: null, label: 'All', count: submissions.length },
                { key: 'GeneralArticles' as SectionCategory, label: 'Articles', count: submissions.filter(s => s.sectionCategory === 'GeneralArticles').length },
                { key: 'TileSpacePurchasers' as SectionCategory, label: 'Tiles', count: submissions.filter(s => s.sectionCategory === 'TileSpacePurchasers').length },
                { key: 'Advertisers' as SectionCategory, label: 'Ads', count: submissions.filter(s => s.sectionCategory === 'Advertisers').length },
                { key: 'SponsorAds' as SectionCategory, label: 'Sponsors', count: submissions.filter(s => s.sectionCategory === 'SponsorAds').length },
                { key: 'MemorialTributes' as SectionCategory, label: 'Tributes', count: submissions.filter(s => s.sectionCategory === 'MemorialTributes').length },
                { key: 'Templates' as SectionCategory, label: 'Templates', count: submissions.filter(s => s.sectionCategory === 'Templates').length },
              ].map(tab => (
                <button
                  key={tab.label}
                  onClick={() => handleFolderClick(tab.key)}
                  style={{
                    padding: '0.5rem 0.85rem',
                    fontSize: '0.78rem',
                    fontWeight: categoryFilter === tab.key ? 600 : 500,
                    cursor: 'pointer',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: categoryFilter === tab.key ? '#1a6b3c' : '#6b7280',
                    borderBottom: categoryFilter === tab.key ? '2px solid #1a6b3c' : '2px solid transparent',
                    marginBottom: '-1px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label} <span style={{ fontSize: '0.68rem', opacity: 0.7 }}>({tab.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Submission Management Section */}
        {activeEdition && (
          <div style={styles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ ...styles.sectionTitle, margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Submissions</h3>
              {canEdit && (
              <button
                onClick={() => setShowSubmissionForm(!showSubmissionForm)}
                style={{ padding: '0.5rem 1rem', backgroundColor: showSubmissionForm ? '#6b7280' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
              >
                {showSubmissionForm ? 'Cancel' : 'New Submission'}
              </button>
              )}
            </div>

            {/* New Submission Form */}
            {showSubmissionForm && (
              <form onSubmit={handleSubmissionSubmit} style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Title *</label>
                    <input
                      type="text"
                      value={submissionForm.title}
                      onChange={(e) => setSubmissionForm({ ...submissionForm, title: e.target.value })}
                      required
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Author Name *</label>
                    <input
                      type="text"
                      value={submissionForm.authorName}
                      onChange={(e) => setSubmissionForm({ ...submissionForm, authorName: e.target.value })}
                      required
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Body</label>
                  <ReactQuill
                    value={submissionForm.body}
                    onChange={(value) => setSubmissionForm({ ...submissionForm, body: value })}
                    theme="snow"
                    style={{ backgroundColor: '#fff', borderRadius: '4px', fontSize: '0.85rem' }}
                    modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Section Category *</label>
                    <select
                      value={submissionForm.sectionCategory}
                      onChange={(e) => setSubmissionForm({ ...submissionForm, sectionCategory: e.target.value as SectionCategory })}
                      required
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                    >
                      <option value="">Select category...</option>
                      {SECTION_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat === 'TileSpacePurchasers' ? 'Tile Space Purchasers' : cat === 'SponsorAds' ? 'Sponsor Ads' : cat === 'WelcomeMessage' ? 'Welcome Message' : cat === 'EventSchedule' ? 'Event Schedule' : cat === 'MemberProfiles' ? 'Member Profiles' : cat === 'MemorialTributes' ? 'Memorial Tributes' : cat === 'PhotoGallery' ? 'Photo Gallery' : cat === 'CommitteeReports' ? 'Committee Reports' : cat === 'GeneralArticles' ? 'General Articles' : cat === 'Templates' ? 'Booklet Templates' : cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Attachment</label>
                    <input
                      type="file"
                      accept=".pdf,.docx,.png,.jpg,.jpeg"
                      onChange={handleFileSelect}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                    />
                    {selectedFile && !fileError && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#374151' }}>
                        {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </div>
                    )}
                    {fileError && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#dc2626' }}>
                        {fileError}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Reference ID</label>
                  <input
                    type="text"
                    value={submissionForm.referenceId}
                    onChange={(e) => setSubmissionForm({ ...submissionForm, referenceId: e.target.value })}
                    placeholder="Optional order/reference ID"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Conditional Booklet Section for GeneralArticles */}
                {submissionForm.sectionCategory === 'GeneralArticles' && (
                  <div style={{ marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '4px', border: '1px solid #93c5fd' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#1e40af' }}>Booklet Section *</label>
                    <select
                      value={submissionForm.bookletSection}
                      onChange={(e) => setSubmissionForm({ ...submissionForm, bookletSection: e.target.value })}
                      required
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' as const, backgroundColor: '#fff' }}
                    >
                      <option value="">Select booklet section...</option>
                      <option value="Front Matter">Front Matter</option>
                      <option value="Feature Stories">Feature Stories</option>
                      <option value="Heritage & History">Heritage & History</option>
                      <option value="Faith & Culture">Faith & Culture</option>
                      <option value="People & Community">People & Community</option>
                      <option value="Culture & Lifestyle">Culture & Lifestyle</option>
                      <option value="KDU Global Impact">KDU Global Impact</option>
                      <option value="Looking Forward">Looking Forward</option>
                      <option value="Congratulatory Message">Congratulatory Message</option>
                      <option value="Sponsors Message">Sponsors Message</option>
                    </select>
                  </div>
                )}

                {/* Conditional Sponsor Ad fields */}
                {submissionForm.sectionCategory === 'SponsorAds' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '4px', border: '1px solid #fcd34d' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Sponsor ID</label>
                      <input
                        type="text"
                        value={submissionForm.sponsorId}
                        onChange={(e) => setSubmissionForm({ ...submissionForm, sponsorId: e.target.value })}
                        placeholder="Enter sponsor ID"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Ad Size</label>
                      <select
                        value={submissionForm.adSize}
                        onChange={(e) => setSubmissionForm({ ...submissionForm, adSize: e.target.value as AdSize })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      >
                        <option value="">Select size...</option>
                        {AD_SIZES.map((size) => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Tile Space Purchaser fields */}
                {submissionForm.sectionCategory === 'TileSpacePurchasers' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#ede9fe', borderRadius: '4px', border: '1px solid #c4b5fd' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Tile Size</label>
                      <select
                        value={submissionForm.adSize}
                        onChange={(e) => setSubmissionForm({ ...submissionForm, adSize: e.target.value as AdSize })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      >
                        <option value="">Select tile size...</option>
                        <option value="FullPage">Full Page</option>
                        <option value="HalfPage">Half Page</option>
                        <option value="QuarterPage">Quarter Page</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Sponsor / Purchaser ID (optional)</label>
                      <input
                        type="text"
                        value={submissionForm.sponsorId}
                        onChange={(e) => setSubmissionForm({ ...submissionForm, sponsorId: e.target.value })}
                        placeholder="Enter purchaser ID"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                )}

                {/* Conditional Tile Memorial & Inscription fields */}
                {isTileCategory && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#f3e8ff', borderRadius: '4px', border: '1px solid #d8b4fe' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }} htmlFor="in-memory-of">In Memory Of</label>
                      <input
                        id="in-memory-of"
                        type="text"
                        value={inMemoryOf}
                        onChange={(e) => setInMemoryOf(e.target.value)}
                        placeholder="Enter honoree name"
                        disabled={submissionSubmitting}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }} htmlFor="inscription-select">Inscription *</label>
                      <select
                        id="inscription-select"
                        value={inscriptionId}
                        onChange={(e) => setInscriptionId(e.target.value)}
                        disabled={submissionSubmitting || inscriptionLoading}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      >
                        <option value="">{inscriptionLoading ? 'Loading...' : 'Select inscription...'}</option>
                        {inscriptionOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Revenue fields for Advertisers / TileSpacePurchasers */}
                {(submissionForm.sectionCategory === 'Advertisers' || submissionForm.sectionCategory === 'TileSpacePurchasers') && (
                  <div style={{ marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#ecfdf5', borderRadius: '4px', border: '1px solid #6ee7b7' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#065f46' }}>Revenue Information</h4>
                    {revenueFormError && (
                      <div style={{ color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.4rem 0.6rem', borderRadius: '4px', marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                        {revenueFormError}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Amount *</label>
                        <input
                          type="number"
                          value={revenueForm.amount}
                          onChange={(e) => { setRevenueForm({ ...revenueForm, amount: e.target.value }); setRevenueFormError(''); }}
                          required
                          min="0.01"
                          step="0.01"
                          placeholder="0.00"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Payment Method *</label>
                        <select
                          value={revenueForm.paymentMethod}
                          onChange={(e) => { setRevenueForm({ ...revenueForm, paymentMethod: e.target.value as PaymentMethod }); setRevenueFormError(''); }}
                          required
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        >
                          <option value="">Select method...</option>
                          {PAYMENT_METHODS.map((method) => (
                            <option key={method} value={method}>{method}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Payment Date *</label>
                        <input
                          type="date"
                          value={revenueForm.paymentDate}
                          onChange={(e) => { setRevenueForm({ ...revenueForm, paymentDate: e.target.value }); setRevenueFormError(''); }}
                          required
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Contact Email</label>
                        <input
                          type="email"
                          value={revenueForm.contactEmail}
                          onChange={(e) => setRevenueForm({ ...revenueForm, contactEmail: e.target.value })}
                          placeholder="email@example.com"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Contact Phone</label>
                        <input
                          type="tel"
                          value={revenueForm.contactPhone}
                          onChange={(e) => setRevenueForm({ ...revenueForm, contactPhone: e.target.value })}
                          placeholder="(555) 123-4567"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Notes</label>
                      <textarea
                        value={revenueForm.notes}
                        onChange={(e) => setRevenueForm({ ...revenueForm, notes: e.target.value })}
                        rows={2}
                        placeholder="Additional notes..."
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submissionSubmitting || uploading || !submissionForm.title || !submissionForm.authorName || !submissionForm.sectionCategory || !!fileError || (isTileCategory && !inscriptionId)}
                  style={{ padding: '0.5rem 1.25rem', backgroundColor: (submissionSubmitting || uploading) ? '#9ca3af' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: (submissionSubmitting || uploading) ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                >
                  {uploading ? 'Uploading...' : submissionSubmitting ? 'Creating...' : 'Create Submission'}
                </button>
              </form>
            )}

            {/* View Mode Toggle: My Reviews / All Submissions */}
            <nav style={{ display: 'flex', gap: '0', marginBottom: '1rem', borderBottom: '1px solid #e2e8e4' }} aria-label="Submissions view">
              <button
                onClick={() => setSubmissionsViewMode('my-reviews')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.82rem',
                  fontWeight: submissionsViewMode === 'my-reviews' ? 600 : 500,
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: submissionsViewMode === 'my-reviews' ? '#1a6b3c' : '#5c6b63',
                  borderBottom: submissionsViewMode === 'my-reviews' ? '2px solid #1a6b3c' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
                aria-current={submissionsViewMode === 'my-reviews' ? 'page' : undefined}
              >
                My Reviews
              </button>
              <button
                onClick={() => setSubmissionsViewMode('all')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.82rem',
                  fontWeight: submissionsViewMode === 'all' ? 600 : 500,
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: submissionsViewMode === 'all' ? '#1a6b3c' : '#5c6b63',
                  borderBottom: submissionsViewMode === 'all' ? '2px solid #1a6b3c' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
                aria-current={submissionsViewMode === 'all' ? 'page' : undefined}
              >
                All Submissions
              </button>
            </nav>

            {/* My Reviews List */}
            {submissionsViewMode === 'my-reviews' && (
              <>
                {myReviewsLoading ? (
                  <p style={{ color: '#666', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>Loading your reviews...</p>
                ) : myReviewsError ? (
                  <div style={{ ...styles.error, marginBottom: '0.75rem' }}>{myReviewsError}</div>
                ) : myReviews.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 1rem' }}>No assignments yet</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {myReviews.map((review) => {
                      const badge = REVIEW_STATUS_BADGES[review.reviewStatus] || REVIEW_STATUS_BADGES['Pending'];
                      return (
                        <div
                          key={review.assignmentId}
                          onClick={() => handleMyReviewClick(review)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.6rem 0.75rem',
                            backgroundColor: selectedSubmission?.id === review.submission.id ? '#f0fdf4' : '#fff',
                            borderRadius: '4px',
                            border: selectedSubmission?.id === review.submission.id ? '1px solid #86efac' : '1px solid #f3f4f6',
                            cursor: 'pointer',
                            transition: 'background-color 0.1s',
                          }}
                          onMouseEnter={(e) => { if (selectedSubmission?.id !== review.submission.id) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                          onMouseLeave={(e) => { if (selectedSubmission?.id !== review.submission.id) e.currentTarget.style.backgroundColor = '#fff'; }}
                        >
                          {/* Title and author */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.submission.title}</div>
                            <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{review.submission.authorName}{review.submission.bookletSection ? ` · ${review.submission.bookletSection}` : ''}</div>
                          </div>
                          {/* Review Status Badge */}
                          <span style={{
                            padding: '0.15rem 0.5rem',
                            backgroundColor: badge.bg,
                            color: badge.text,
                            borderRadius: '10px',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            marginLeft: '0.5rem',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                          }}>
                            {badge.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Show All Items toggle button */}
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    onClick={() => setSubmissionsViewMode('all')}
                    style={{
                      padding: '0.4rem 1rem',
                      backgroundColor: 'transparent',
                      color: '#1a6b3c',
                      border: '1px solid #1a6b3c',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    aria-label="Show all submissions"
                  >
                    Show All Items
                  </button>
                </div>
              </>
            )}

            {/* All Submissions List */}
            {submissionsViewMode === 'all' && (
            <>
            {/* Back to My Reviews button (shown for Delegates) */}
            {authUser?.role === 'Delegate' && (
              <div style={{ marginBottom: '0.75rem' }}>
                <button
                  onClick={() => setSubmissionsViewMode('my-reviews')}
                  style={{
                    padding: '0.35rem 0.85rem',
                    backgroundColor: 'transparent',
                    color: '#1a6b3c',
                    border: '1px solid #1a6b3c',
                    borderRadius: '4px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  aria-label="Back to My Reviews"
                >
                  ← Back to My Reviews
                </button>
              </div>
            )}
            {submissions.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>No submissions yet. Create one to get started.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {submissions.map((sub) => {
                  const isAssignedToMe = myReviews.some((r) => r.submission.id === sub.id);
                  return (
                  <div key={sub.id}>
                    <div
                      onClick={() => {
                        if (authUser?.role === 'Delegate') {
                          handleAllSubmissionsClick(sub);
                        } else {
                          handleSubmissionClick(sub);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: selectedSubmission?.id === sub.id ? '#f0fdf4' : '#fff',
                        borderRadius: selectedSubmission?.id === sub.id ? '4px 4px 0 0' : '4px',
                        border: selectedSubmission?.id === sub.id ? '1px solid #86efac' : '1px solid #f3f4f6',
                        borderBottom: selectedSubmission?.id === sub.id ? 'none' : '1px solid #f3f4f6',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => { if (selectedSubmission?.id !== sub.id) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { if (selectedSubmission?.id !== sub.id) e.currentTarget.style.backgroundColor = '#fff'; }}
                    >
                      {/* Status dot */}
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: STATUS_BADGE_COLORS[sub.status], flexShrink: 0, marginRight: '0.6rem' }} />
                      {/* Title and author */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.title}</div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{sub.authorName}</div>
                      </div>
                      {/* Assignment indicator badge (green) for submissions assigned to current user */}
                      {isAssignedToMe && (
                        <span style={{
                          padding: '0.1rem 0.45rem',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          marginLeft: '0.5rem',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}>
                          📋 Assigned
                        </span>
                      )}
                      {/* Revenue badge */}
                      {sub.spaceRevenue && (
                        <span style={{ padding: '0.1rem 0.4rem', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 600, marginLeft: '0.5rem', flexShrink: 0 }}>
                          ${Number(sub.spaceRevenue.amount).toFixed(0)}
                        </span>
                      )}
                      {/* Status label */}
                      <span style={{ fontSize: '0.65rem', fontWeight: 500, color: '#6b7280', marginLeft: '0.5rem', flexShrink: 0, minWidth: '55px', textAlign: 'right' }}>
                        {sub.status === 'UnderReview' ? 'Review' : sub.status === 'ReadyForLayout' ? 'Ready' : sub.status === 'RevisionRequested' ? 'Revision' : sub.status}
                      </span>
                    </div>

                    {/* Expanded Detail Panel */}
                    {selectedSubmission?.id === sub.id && (
                      <div style={{
                        padding: '1rem 1.25rem',
                        backgroundColor: '#fff',
                        border: '1px solid #1a6b3c',
                        borderTop: 'none',
                        borderRadius: '0 0 6px 6px',
                      }}>
                        {detailLoading ? (
                          <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading details...</p>
                        ) : (
                          <>
                            {detailError && <div style={{ ...styles.error, marginBottom: '0.75rem' }}>{detailError}</div>}

                            {/* Edit Button */}
                            {!editingSubmission && canEditContent && (
                              <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => { setEditingSubmission(true); setEditForm({ title: selectedSubmission.title, body: selectedSubmission.body || '', bookletSection: selectedSubmission.bookletSection || '', referenceId: selectedSubmission.referenceId || '', sectionCategory: selectedSubmission.sectionCategory, authorName: selectedSubmission.authorName || '', inMemoryOf: selectedSubmission.inMemoryOf || '', inscriptionId: selectedSubmission.inscriptionId || '' }); setEditRevenueForm({ amount: selectedSubmission.spaceRevenue?.amount?.toString() || '', paymentMethod: (selectedSubmission.spaceRevenue?.paymentMethod || '') as PaymentMethod | '', paymentDate: selectedSubmission.spaceRevenue?.paymentDate ? selectedSubmission.spaceRevenue.paymentDate.split('T')[0] : '', contactEmail: selectedSubmission.spaceRevenue?.contactEmail || '', contactPhone: selectedSubmission.spaceRevenue?.contactPhone || '', notes: selectedSubmission.spaceRevenue?.notes || '' }); setEditRegistrationId(selectedSubmission.registrationId || ''); setEditRegistrationSearch(selectedSubmission.registration?.user?.name || selectedSubmission.registration?.user?.email || ''); setRegistrationResults([]); setEditSelectedFile(null); setEditFileError(''); }}
                                  style={{ padding: '0.35rem 0.75rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                                >
                                  ✏️ Edit Submission
                                </button>
                              </div>
                            )}

                            {/* Edit Form */}
                            {editingSubmission && canEditContent && (
                              <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fffbeb', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Edit Submission</h4>
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Title</label>
                                  <input
                                    type="text"
                                    value={editForm.title}
                                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Author Name</label>
                                  <input
                                    type="text"
                                    value={editForm.authorName || ''}
                                    onChange={(e) => setEditForm({ ...editForm, authorName: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Section Category</label>
                                  <select
                                    value={editForm.sectionCategory}
                                    onChange={(e) => setEditForm({ ...editForm, sectionCategory: e.target.value as SectionCategory })}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' as const, backgroundColor: '#fff' }}
                                  >
                                    {SECTION_CATEGORIES.map((cat) => (
                                      <option key={cat} value={cat}>{cat === 'TileSpacePurchasers' ? 'Tile Space Purchasers' : cat === 'SponsorAds' ? 'Sponsor Ads' : cat === 'WelcomeMessage' ? 'Welcome Message' : cat === 'EventSchedule' ? 'Event Schedule' : cat === 'MemberProfiles' ? 'Member Profiles' : cat === 'MemorialTributes' ? 'Memorial Tributes' : cat === 'PhotoGallery' ? 'Photo Gallery' : cat === 'CommitteeReports' ? 'Committee Reports' : cat === 'GeneralArticles' ? 'General Articles' : cat === 'Templates' ? 'Booklet Templates' : cat}</option>
                                    ))}
                                  </select>
                                </div>
                                {/* In Memory Of and Inscription — shown right after category for Tile Space Purchasers */}
                                {editForm.sectionCategory === 'TileSpacePurchasers' && (
                                  <div style={{ marginBottom: '0.5rem', padding: '0.75rem', backgroundColor: '#f3e8ff', borderRadius: '4px', border: '1px solid #d8b4fe' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>In Memory Of</label>
                                        <input
                                          type="text"
                                          value={editForm.inMemoryOf}
                                          onChange={(e) => setEditForm({ ...editForm, inMemoryOf: e.target.value })}
                                          placeholder="Enter honoree name"
                                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                        />
                                      </div>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Inscription</label>
                                        <select
                                          value={editForm.inscriptionId}
                                          onChange={(e) => setEditForm({ ...editForm, inscriptionId: e.target.value })}
                                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box', backgroundColor: '#fff' }}
                                        >
                                          <option value="">{inscriptionLoading ? 'Loading...' : 'Select inscription...'}</option>
                                          {inscriptionOptions.map((opt) => (
                                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Body / Content</label>
                                  <ReactQuill
                                    value={editForm.body}
                                    onChange={(value) => setEditForm({ ...editForm, body: value })}
                                    theme="snow"
                                    style={{ backgroundColor: '#fff', borderRadius: '4px', fontSize: '0.85rem' }}
                                    modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                                  />
                                </div>
                                {editForm.sectionCategory === 'GeneralArticles' && (
                                  <div style={{ marginBottom: '0.5rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#1e40af' }}>Booklet Section</label>
                                    <select
                                      value={editForm.bookletSection}
                                      onChange={(e) => setEditForm({ ...editForm, bookletSection: e.target.value })}
                                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' as const, backgroundColor: '#fff' }}
                                    >
                                      <option value="">Select booklet section...</option>
                                      <option value="Front Matter">Front Matter</option>
                                      <option value="Feature Stories">Feature Stories</option>
                                      <option value="Heritage & History">Heritage &amp; History</option>
                                      <option value="Faith & Culture">Faith &amp; Culture</option>
                                      <option value="People & Community">People &amp; Community</option>
                                      <option value="Culture & Lifestyle">Culture &amp; Lifestyle</option>
                                      <option value="KDU Global Impact">KDU Global Impact</option>
                                      <option value="Looking Forward">Looking Forward</option>
                                      <option value="Congratulatory Message">Congratulatory Message</option>
                                      <option value="Sponsors Message">Sponsors Message</option>
                                    </select>
                                  </div>
                                )}
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Reference ID</label>
                                  <input
                                    type="text"
                                    value={editForm.referenceId}
                                    onChange={(e) => setEditForm({ ...editForm, referenceId: e.target.value })}
                                    placeholder="Optional order/reference ID"
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                  />
                                </div>
                                {/* Author Name field for Tile Space Purchasers */}
                                {(editForm.sectionCategory === 'TileSpacePurchasers' || editForm.sectionCategory === 'Advertisers') && (
                                  <div style={{ marginBottom: '0.5rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Purchaser / Author Name</label>
                                    <input
                                      type="text"
                                      value={editForm.authorName}
                                      onChange={(e) => setEditForm({ ...editForm, authorName: e.target.value })}
                                      placeholder="Name of the purchaser"
                                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                    />
                                  </div>
                                )}
                                {/* Linked Registration for Tile Space Purchasers / Advertisers */}
                                {(editForm.sectionCategory === 'TileSpacePurchasers' || editForm.sectionCategory === 'Advertisers') && (
                                  <div style={{ marginBottom: '0.5rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Linked Registration</label>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                      <input
                                        type="text"
                                        value={editRegistrationSearch}
                                        onChange={(e) => { setEditRegistrationSearch(e.target.value); searchRegistrations(e.target.value); }}
                                        placeholder="Search by name or email..."
                                        style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                      />
                                      {editRegistrationId && (
                                        <button
                                          type="button"
                                          onClick={() => { setEditRegistrationId(''); setEditRegistrationSearch(''); setRegistrationResults([]); }}
                                          style={{ padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '0.75rem', color: '#6b7280' }}
                                        >
                                          Unlink
                                        </button>
                                      )}
                                    </div>
                                    {/* Search results dropdown */}
                                    {registrationResults.length > 0 && !editRegistrationId && (
                                      <div style={{ marginTop: '0.25rem', border: '1px solid #e5e7eb', borderRadius: '4px', maxHeight: '120px', overflowY: 'auto', backgroundColor: '#fff' }}>
                                        {registrationResults.map((reg: { id: string; name: string; email: string }) => (
                                          <div
                                            key={reg.id}
                                            onClick={() => { setEditRegistrationId(reg.id); setEditRegistrationSearch(reg.name || reg.email); setRegistrationResults([]); }}
                                            style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid #f3f4f6' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                                          >
                                            <span style={{ fontWeight: 500 }}>{reg.name || 'No name'}</span>
                                            <span style={{ marginLeft: '0.5rem', color: '#9ca3af', fontSize: '0.72rem' }}>{reg.email}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {editRegistrationId && (
                                      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#059669' }}>
                                        ✓ Linked to registration: {editRegistrationSearch || editRegistrationId}
                                      </div>
                                    )}
                                    {selectedSubmission.registration && !editRegistrationId && (
                                      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                        Previously linked to: {selectedSubmission.registration.user?.name || selectedSubmission.registration.user?.email || selectedSubmission.registrationId}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Revenue info note for Tile Space Purchasers */}
                                {(editForm.sectionCategory === 'TileSpacePurchasers' || editForm.sectionCategory === 'Advertisers') && (
                                  <div style={{ marginBottom: '0.5rem', padding: '0.75rem', backgroundColor: '#ecfdf5', borderRadius: '4px', border: '1px solid #6ee7b7' }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#065f46', marginBottom: '0.5rem' }}>Revenue Information</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem', color: '#374151' }}>Amount ($)</label>
                                        <input
                                          type="number"
                                          value={editRevenueForm.amount}
                                          onChange={(e) => setEditRevenueForm({ ...editRevenueForm, amount: e.target.value })}
                                          min="0"
                                          step="0.01"
                                          placeholder="0.00"
                                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                        />
                                      </div>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem', color: '#374151' }}>Payment Method</label>
                                        <select
                                          value={editRevenueForm.paymentMethod}
                                          onChange={(e) => setEditRevenueForm({ ...editRevenueForm, paymentMethod: e.target.value as PaymentMethod })}
                                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box', backgroundColor: '#fff' }}
                                        >
                                          <option value="">Select...</option>
                                          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                      </div>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem', color: '#374151' }}>Payment Date</label>
                                        <input
                                          type="date"
                                          value={editRevenueForm.paymentDate}
                                          onChange={(e) => setEditRevenueForm({ ...editRevenueForm, paymentDate: e.target.value })}
                                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                        />
                                      </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem', color: '#374151' }}>Contact Email</label>
                                        <input
                                          type="email"
                                          value={editRevenueForm.contactEmail}
                                          onChange={(e) => setEditRevenueForm({ ...editRevenueForm, contactEmail: e.target.value })}
                                          placeholder="email@example.com"
                                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                        />
                                      </div>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem', color: '#374151' }}>Contact Phone</label>
                                        <input
                                          type="text"
                                          value={editRevenueForm.contactPhone}
                                          onChange={(e) => setEditRevenueForm({ ...editRevenueForm, contactPhone: e.target.value })}
                                          placeholder="Phone number"
                                          style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem', color: '#374151' }}>Notes</label>
                                      <input
                                        type="text"
                                        value={editRevenueForm.notes}
                                        onChange={(e) => setEditRevenueForm({ ...editRevenueForm, notes: e.target.value })}
                                        placeholder="Optional notes"
                                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }}
                                      />
                                    </div>
                                  </div>
                                )}
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Attachment</label>
                                  {selectedSubmission.attachmentUrl && !editSelectedFile && (
                                    <div style={{ marginBottom: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                      Current: {selectedSubmission.attachmentUrl.split('/').pop()}
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept=".pdf,.docx,.png,.jpg,.jpeg"
                                    onChange={handleEditFileSelect}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                                  />
                                  {editSelectedFile && !editFileError && (
                                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#374151' }}>
                                      {editSelectedFile.name} ({formatFileSize(editSelectedFile.size)})
                                    </div>
                                  )}
                                  {editFileError && (
                                    <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#dc2626' }}>
                                      {editFileError}
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={handleSaveEdit}
                                    disabled={editSaving || uploading || !editForm.title.trim() || !!editFileError}
                                    style={{ padding: '0.4rem 0.75rem', backgroundColor: (editSaving || uploading) || !editForm.title.trim() || !!editFileError ? '#9ca3af' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: (editSaving || uploading) ? 'not-allowed' : 'pointer' }}
                                  >
                                    {uploading ? 'Uploading...' : editSaving ? 'Saving...' : 'Save Changes'}
                                  </button>
                                  <button
                                    onClick={() => setEditingSubmission(false)}
                                    style={{ padding: '0.4rem 0.75rem', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Full Body Content */}
                            {selectedSubmission.body && (
                              <div style={{ marginBottom: '1rem' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Content</h4>
                                <div
                                  dangerouslySetInnerHTML={{ __html: selectedSubmission.body }}
                                  style={{ padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb', fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}
                                />
                              </div>
                            )}

                            {/* Attachment Download */}
                            {selectedSubmission.attachmentUrl && (
                              <div style={{ marginBottom: '1rem' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Attachment</h4>
                                <a
                                  href={`${import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000')}/public/uploads/${selectedSubmission.attachmentUrl.split('/').pop()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', color: '#4f46e5', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}
                                >
                                  📄 {selectedSubmission.attachmentUrl.split('/').pop()}
                                  <span style={{ fontSize: '0.75rem', color: '#6366f1', marginLeft: '0.25rem' }}>↗ View / Download</span>
                                </a>
                              </div>
                            )}

                            {/* Booklet Section (for GeneralArticles) */}
                            {selectedSubmission.sectionCategory === 'GeneralArticles' && (
                              <div style={{ marginBottom: '1rem' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Booklet Section</h4>
                                <div style={{ padding: '0.5rem 0.75rem', backgroundColor: '#eff6ff', borderRadius: '4px', border: '1px solid #93c5fd', fontSize: '0.85rem', color: '#1e40af', fontWeight: 500 }}>
                                  {selectedSubmission.bookletSection || 'Not assigned'}
                                </div>
                              </div>
                            )}

                            {/* Revenue Information Section */}
                            {selectedSubmission.spaceRevenue && (
                              <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Revenue Information</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.8rem' }}>
                                  <div>
                                    <span style={{ color: '#6b7280' }}>Amount: </span>
                                    <span style={{ fontWeight: 600, color: '#166534' }}>${Number(selectedSubmission.spaceRevenue.amount).toFixed(2)}</span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#6b7280' }}>Payment Method: </span>
                                    <span style={{ fontWeight: 600, color: '#374151' }}>{selectedSubmission.spaceRevenue.paymentMethod}</span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#6b7280' }}>Payment Date: </span>
                                    <span style={{ fontWeight: 600, color: '#374151' }}>{new Date(selectedSubmission.spaceRevenue.paymentDate).toLocaleDateString()}</span>
                                  </div>
                                  {selectedSubmission.spaceRevenue.contactEmail && (
                                    <div>
                                      <span style={{ color: '#6b7280' }}>Email: </span>
                                      <span style={{ fontWeight: 600, color: '#374151' }}>{selectedSubmission.spaceRevenue.contactEmail}</span>
                                    </div>
                                  )}
                                  {selectedSubmission.spaceRevenue.contactPhone && (
                                    <div>
                                      <span style={{ color: '#6b7280' }}>Phone: </span>
                                      <span style={{ fontWeight: 600, color: '#374151' }}>{selectedSubmission.spaceRevenue.contactPhone}</span>
                                    </div>
                                  )}
                                  {selectedSubmission.spaceRevenue.notes && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <span style={{ color: '#6b7280' }}>Notes: </span>
                                      <span style={{ color: '#374151' }}>{selectedSubmission.spaceRevenue.notes}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Status Change Section */}
                            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                              <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Change Status</h4>
                              {VALID_TRANSITIONS[selectedSubmission.status].length === 0 ? (
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>No further transitions available.</p>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <select
                                    value={statusChangeTarget}
                                    onChange={(e) => setStatusChangeTarget(e.target.value as SubmissionStatus)}
                                    style={{ padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}
                                  >
                                    <option value="">Select next status...</option>
                                    {VALID_TRANSITIONS[selectedSubmission.status].map((s) => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={handleStatusChange}
                                    disabled={!statusChangeTarget || statusChanging}
                                    style={{
                                      padding: '0.4rem 0.75rem',
                                      backgroundColor: statusChangeTarget && !statusChanging ? '#1a6b3c' : '#9ca3af',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: statusChangeTarget && !statusChanging ? 'pointer' : 'not-allowed',
                                      fontSize: '0.8rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {statusChanging ? 'Changing...' : 'Change Status'}
                                  </button>
                                </div>
                              )}
                              {statusChangeError && (
                                <div style={{ marginTop: '0.5rem', color: '#d32f2f', fontSize: '0.8rem', backgroundColor: '#fdecea', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                                  {statusChangeError}
                                </div>
                              )}
                            </div>

                            {/* Status History Timeline */}
                            {selectedSubmission.statusLogs && selectedSubmission.statusLogs.length > 0 && (
                              <div style={{ marginBottom: '1rem' }}>
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Status History</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                  {selectedSubmission.statusLogs.map((log) => (
                                    <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.35rem 0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                                      <span style={{ color: '#6b7280' }}>
                                        {log.fromStatus ? (
                                          <>{log.fromStatus} → {log.toStatus}</>
                                        ) : (
                                          <>Created as {log.toStatus}</>
                                        )}
                                      </span>
                                      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '0.75rem' }}>
                                        {new Date(log.changedAt).toLocaleDateString()} {new Date(log.changedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Reviewers Section */}
                            <div style={{ marginBottom: '1rem' }}>
                              <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Reviewers</h4>
                              {selectedSubmission.reviewers && selectedSubmission.reviewers.length > 0 ? (
                                <>
                                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.4rem' }}>
                                    Reviews: {selectedSubmission.reviewers.filter((r) => r.reviewed).length}/{selectedSubmission.reviewers.length} complete
                                  </p>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.5rem' }}>
                                    {selectedSubmission.reviewers.map((rev) => {
                                      const isOwnReview = rev.member?.userId === authUser?.sub;
                                      const canToggle = canEditContent || isOwnReview;
                                      return (
                                      <div key={rev.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.35rem 0.5rem', backgroundColor: rev.reviewed ? '#f0fdf4' : '#f9fafb', borderRadius: '4px', border: `1px solid ${rev.reviewed ? '#bbf7d0' : '#e5e7eb'}`, fontSize: '0.8rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <input
                                            type="checkbox"
                                            checked={rev.reviewed}
                                            onChange={() => handleToggleReviewed(rev.memberId)}
                                            disabled={!canToggle}
                                            title={rev.reviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
                                            aria-label={`Mark ${rev.member ? `${rev.member.firstName} ${rev.member.lastName}` : rev.memberId} as ${rev.reviewed ? 'not reviewed' : 'reviewed'}`}
                                            style={{ cursor: canToggle ? 'pointer' : 'not-allowed', accentColor: '#1a6b3c' }}
                                          />
                                          <span style={{ color: '#374151' }}>
                                            {rev.member ? `${rev.member.firstName} ${rev.member.lastName}` : rev.memberId}
                                          </span>
                                          {rev.reviewed && rev.reviewedAt && (
                                            <span style={{ color: '#9ca3af', fontSize: '0.7rem', fontStyle: 'italic' }}>
                                              reviewed {new Date(rev.reviewedAt).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                            {new Date(rev.assignedAt).toLocaleDateString()}
                                          </span>
                                          {canEditContent && (
                                          <button
                                            onClick={() => handleRemoveReviewer(rev.memberId)}
                                            style={{ padding: '0.2rem 0.5rem', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '3px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}
                                          >
                                            Remove
                                          </button>
                                          )}
                                        </div>
                                      </div>
                                      );
                                    })}
                                  </div>
                                </>
                              ) : (
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>No reviewers assigned.</p>
                              )}
                              {canEditContent && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {(() => { const availableReviewers = getAvailableReviewers(); return (
                                  <>
                                    <select
                                      value={reviewerInput}
                                      onChange={(e) => setReviewerInput(e.target.value)}
                                      disabled={assigningReviewer || availableReviewers.length === 0}
                                      style={{ padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', flex: 1, maxWidth: '250px' }}
                                    >
                                      <option value="">
                                        {availableReviewers.length === 0 ? 'All members assigned' : 'Select a reviewer...'}
                                      </option>
                                      {availableReviewers.map((m) => (
                                        <option key={m.id} value={m.id}>
                                          {m.firstName} {m.lastName}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={handleAssignReviewer}
                                      disabled={!reviewerInput || assigningReviewer}
                                      style={{
                                        padding: '0.4rem 0.75rem',
                                        backgroundColor: reviewerInput && !assigningReviewer ? '#1a6b3c' : '#9ca3af',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: reviewerInput && !assigningReviewer ? 'pointer' : 'not-allowed',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {assigningReviewer ? 'Assigning...' : 'Assign Reviewer'}
                                    </button>
                                  </>
                                ); })()}
                              </div>
                              )}
                            </div>

                            {/* Comments Section */}
                            <div style={{ marginBottom: '1rem' }}>
                              <h4 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>Comments</h4>
                              {selectedSubmission.comments && selectedSubmission.comments.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.75rem' }}>
                                  {selectedSubmission.comments.map((comment) => (
                                    <div key={comment.id} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb', fontSize: '0.8rem' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                        <span style={{ fontWeight: 600, color: '#374151' }}>{comment.commenterName}</span>
                                        <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                          {new Date(comment.createdAt).toLocaleDateString()} {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      </div>
                                      <p style={{ margin: 0, color: '#4b5563' }}>{comment.text}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>No comments yet.</p>
                              )}
                              {(canEditContent || selectedSubmission.reviewers?.some(r => r.member?.userId === authUser?.sub)) && (
                              <form onSubmit={handleAddComment} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <input
                                    type="text"
                                    value={commentForm.commenterName}
                                    onChange={(e) => setCommentForm({ ...commentForm, commenterName: e.target.value })}
                                    placeholder="Your name"
                                    required
                                    style={{ padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', width: '150px' }}
                                  />
                                  <input
                                    type="text"
                                    value={commentForm.text}
                                    onChange={(e) => setCommentForm({ ...commentForm, text: e.target.value })}
                                    placeholder="Add a comment..."
                                    required
                                    style={{ padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', flex: 1 }}
                                  />
                                  <button
                                    type="submit"
                                    disabled={!commentForm.text.trim() || !commentForm.commenterName.trim() || addingComment}
                                    style={{
                                      padding: '0.4rem 0.75rem',
                                      backgroundColor: commentForm.text.trim() && commentForm.commenterName.trim() && !addingComment ? '#1a6b3c' : '#9ca3af',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: commentForm.text.trim() && commentForm.commenterName.trim() && !addingComment ? 'pointer' : 'not-allowed',
                                      fontSize: '0.8rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {addingComment ? 'Adding...' : 'Add Comment'}
                                  </button>
                                </div>
                              </form>
                              )}
                            </div>

                            {/* Reviewer Content Editor (for Delegates who are reviewers) */}
                            {selectedSubmission && !canEditContent && selectedSubmission.reviewers?.some(r => r.member?.userId === authUser?.sub) && (
                              <ReviewerContentEditor
                                submissionId={selectedSubmission.id}
                                currentMemberId={selectedSubmission.reviewers?.find(r => r.member?.userId === authUser?.sub)?.memberId || null}
                                userRole={authUser?.role || 'Attendee'}
                              />
                            )}

                            {/* Reviewer Content Viewer (for Organizer/Admin) */}
                            {selectedSubmission && canEditContent && (
                              <ReviewerContentEditor
                                submissionId={selectedSubmission.id}
                                currentMemberId={selectedSubmission.reviewers?.find(r => r.member?.userId === authUser?.sub)?.memberId || null}
                                userRole={authUser?.role || 'Organizer'}
                              />
                            )}

                            {/* File Upload — Organizer/Admin only */}
                            {canEditContent && selectedSubmission && (
                              <ReviewerUploadControl
                                submissionId={selectedSubmission.id}
                                currentAttachmentUrl={selectedSubmission.attachmentUrl}
                                onUploadSuccess={(newUrl) => {
                                  setSelectedSubmission({ ...selectedSubmission, attachmentUrl: newUrl });
                                }}
                              />
                            )}

                            {/* Version Count & List */}
                            <div>
                              <div
                                onClick={() => setShowVersions(!showVersions)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                              >
                                <h4 style={{ fontSize: '0.85rem', color: '#374151', margin: 0, fontWeight: 600 }}>Versions</h4>
                                <span style={{
                                  padding: '0.15rem 0.5rem',
                                  backgroundColor: '#e0e7ff',
                                  color: '#3730a3',
                                  borderRadius: '9999px',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                }}>
                                  {selectedSubmission.versions?.length || 0}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                  {showVersions ? '▲' : '▼'}
                                </span>
                              </div>
                              {showVersions && selectedSubmission.versions && selectedSubmission.versions.length > 0 && (
                                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                  {selectedSubmission.versions.map((ver) => (
                                    <div key={ver.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.35rem 0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb', fontSize: '0.8rem' }}>
                                      <span style={{ color: '#374151' }}>
                                        <strong>v{ver.versionNumber}</strong> — {ver.title}
                                      </span>
                                      <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                        {new Date(ver.savedAt).toLocaleDateString()} {new Date(ver.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {showVersions && (!selectedSubmission.versions || selectedSubmission.versions.length === 0) && (
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>No previous versions.</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderLayoutTab() {
    if (!activeEdition) {
      return (
        <div style={styles.emptyState}>
          <p>No active edition. Create one in the Submissions tab to get started.</p>
        </div>
      );
    }

    async function handleAssignPage(submissionId: string) {
      const pageStr = pageInputs[submissionId];
      const pageNum = parseInt(pageStr, 10);
      if (!pageStr || isNaN(pageNum) || pageNum < 1) return;

      setLayoutAssigning(submissionId);
      setError('');
      try {
        await apiClient(`/weekend-booklet/submissions/${submissionId}`, {
          method: 'PATCH',
          body: { pageNumber: pageNum },
        });
        // Clear the input and refresh layout data
        setPageInputs((prev) => {
          const next = { ...prev };
          delete next[submissionId];
          return next;
        });
        await fetchLayoutData();
      } catch (err: unknown) {
        const apiErr = err as { message?: string };
        setError(apiErr.message || 'Failed to assign page number.');
      } finally {
        setLayoutAssigning(null);
      }
    }

    return (
      <div>
        {/* Page Allocated Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Page Allocated</h3>
          {layoutAllocated.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'center' as const, padding: '1rem' }}>
              No submissions have been assigned to pages yet.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {layoutAllocated.map((sub) => (
                <div
                  key={sub.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    gap: '1rem',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '50%',
                      backgroundColor: '#1a6b3c',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      flexShrink: 0,
                    }}
                  >
                    {sub.pageNumber}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{sub.title}</span>
                    <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>by {sub.authorName}</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' as const }}>{sub.sectionCategory}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Unallocated Ready for Layout Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Ready for Layout (Unallocated)</h3>
          {layoutUnallocated.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'center' as const, padding: '1rem' }}>
              No unallocated submissions ready for layout.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {layoutUnallocated.map((sub) => {
                const pageValue = pageInputs[sub.id] || '';
                const pageNum = parseInt(pageValue, 10);
                const exceedsTarget =
                  activeEdition?.targetPageCount != null &&
                  !isNaN(pageNum) &&
                  pageNum > activeEdition.targetPageCount;

                return (
                  <div
                    key={sub.id}
                    style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{sub.title}</span>
                        <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>by {sub.authorName}</span>
                        <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: '#9ca3af' }}>{sub.sectionCategory}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="number"
                        min="1"
                        placeholder="Page #"
                        value={pageValue}
                        onChange={(e) => setPageInputs((prev) => ({ ...prev, [sub.id]: e.target.value }))}
                        style={{
                          width: '5rem',
                          padding: '0.4rem 0.5rem',
                          border: exceedsTarget ? '2px solid #d97706' : '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box' as const,
                        }}
                      />
                      <button
                        onClick={() => handleAssignPage(sub.id)}
                        disabled={!pageValue || isNaN(pageNum) || pageNum < 1 || layoutAssigning === sub.id}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: (!pageValue || isNaN(pageNum) || pageNum < 1 || layoutAssigning === sub.id) ? '#9ca3af' : '#1a6b3c',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: (!pageValue || isNaN(pageNum) || pageNum < 1 || layoutAssigning === sub.id) ? 'not-allowed' : 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                        }}
                      >
                        {layoutAssigning === sub.id ? 'Assigning...' : 'Assign Page'}
                      </button>
                      {exceedsTarget && (
                        <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                          ⚠ Exceeds target page count ({activeEdition.targetPageCount})
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderPrintSalesTab() {
    if (!activeEdition) {
      return (
        <div style={styles.emptyState}>
          <p>No active edition. Create one in the Submissions tab to manage print orders.</p>
        </div>
      );
    }

    const PRINT_ORDER_STATUS_COLORS: Record<PrintOrderStatus, string> = {
      Ordered: '#2563eb',
      Delivered: '#16a34a',
    };

    async function handleCreatePrintOrder(e: React.FormEvent) {
      e.preventDefault();
      if (!activeEdition || !printOrderForm.quantity || !printOrderForm.printerVendor) return;
      setPrintOrderSubmitting(true);
      setError('');
      try {
        const payload: Record<string, unknown> = {
          quantity: parseInt(printOrderForm.quantity, 10),
          printerVendor: printOrderForm.printerVendor,
        };
        if (printOrderForm.totalCost) payload.totalCost = parseFloat(printOrderForm.totalCost);
        if (printOrderForm.orderDate) payload.orderDate = new Date(printOrderForm.orderDate).toISOString();
        if (printOrderForm.expectedDeliveryDate) payload.expectedDeliveryDate = new Date(printOrderForm.expectedDeliveryDate).toISOString();

        await apiClient(`/weekend-booklet/editions/${activeEdition.id}/print-orders`, { method: 'POST', body: payload });
        setPrintOrderForm({ quantity: '', printerVendor: '', totalCost: '', orderDate: '', expectedDeliveryDate: '' });
        setShowPrintOrderForm(false);
        await loadData();
      } catch (err: unknown) {
        const apiErr = err as { message?: string };
        setError(apiErr.message || 'Failed to create print order.');
      } finally {
        setPrintOrderSubmitting(false);
      }
    }

    async function handleMarkDelivered(orderId: string) {
      setError('');
      try {
        await apiClient(`/weekend-booklet/print-orders/${orderId}`, { method: 'PATCH', body: { status: 'Delivered' } });
        await loadData();
      } catch (err: unknown) {
        const apiErr = err as { message?: string };
        setError(apiErr.message || 'Failed to update print order.');
      }
    }

    return (
      <div>
        {/* Print Orders Section */}
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ ...styles.sectionTitle, margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Print Orders</h3>
            <button
              onClick={() => setShowPrintOrderForm(!showPrintOrderForm)}
              style={{ padding: '0.5rem 1rem', backgroundColor: showPrintOrderForm ? '#6b7280' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
            >
              {showPrintOrderForm ? 'Cancel' : 'Create Print Order'}
            </button>
          </div>

          {/* Create Print Order Form */}
          {showPrintOrderForm && (
            <form onSubmit={handleCreatePrintOrder} style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Quantity *</label>
                  <input
                    type="number"
                    value={printOrderForm.quantity}
                    onChange={(e) => setPrintOrderForm({ ...printOrderForm, quantity: e.target.value })}
                    required
                    min="1"
                    placeholder="e.g. 500"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Printer Vendor *</label>
                  <input
                    type="text"
                    value={printOrderForm.printerVendor}
                    onChange={(e) => setPrintOrderForm({ ...printOrderForm, printerVendor: e.target.value })}
                    required
                    placeholder="e.g. PrintCo Inc."
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Total Cost</label>
                  <input
                    type="number"
                    value={printOrderForm.totalCost}
                    onChange={(e) => setPrintOrderForm({ ...printOrderForm, totalCost: e.target.value })}
                    min="0"
                    step="0.01"
                    placeholder="e.g. 1500.00"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Order Date</label>
                  <input
                    type="date"
                    value={printOrderForm.orderDate}
                    onChange={(e) => setPrintOrderForm({ ...printOrderForm, orderDate: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Expected Delivery Date</label>
                  <input
                    type="date"
                    value={printOrderForm.expectedDeliveryDate}
                    onChange={(e) => setPrintOrderForm({ ...printOrderForm, expectedDeliveryDate: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={printOrderSubmitting || !printOrderForm.quantity || !printOrderForm.printerVendor}
                style={{ padding: '0.5rem 1.25rem', backgroundColor: printOrderSubmitting ? '#9ca3af' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: printOrderSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
              >
                {printOrderSubmitting ? 'Creating...' : 'Create Print Order'}
              </button>
            </form>
          )}

          {/* Print Orders List */}
          {printOrders.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>No print orders yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {printOrders.map((order) => (
                <div
                  key={order.id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                >
                  <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Quantity</span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{order.quantity}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Vendor</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{order.printerVendor}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Cost</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{order.totalCost != null ? `$${Number(order.totalCost).toFixed(2)}` : '—'}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Order Date</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{new Date(order.orderDate).toLocaleDateString()}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Expected Delivery</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span
                      style={{
                        padding: '0.2rem 0.6rem',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: PRINT_ORDER_STATUS_COLORS[order.status],
                      }}
                    >
                      {order.status}
                    </span>
                    {order.status === 'Ordered' && (
                      <button
                        onClick={() => handleMarkDelivered(order.id)}
                        style={{ padding: '0.3rem 0.6rem', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                      >
                        Mark Delivered
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pricing Section */}
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, margin: 0, borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem', marginBottom: '1rem' }}>Pricing</h3>

          {/* Current Price */}
          <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Price Per Copy</span>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a6b3c', marginTop: '0.25rem' }}>
              {pricing.length > 0 ? `$${Number(pricing[0].pricePerCopy).toFixed(2)}` : '— (not set)'}
            </div>
          </div>

          {/* Set Price Form */}
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!activeEdition || !priceInput) return;
              setPricingSubmitting(true);
              setError('');
              try {
                await apiClient(`/weekend-booklet/editions/${activeEdition.id}/pricing`, {
                  method: 'POST',
                  body: { pricePerCopy: parseFloat(priceInput) },
                });
                setPriceInput('');
                // Refresh pricing data
                const updatedPricing = await apiClient<BookletPricing[]>(`/weekend-booklet/editions/${activeEdition.id}/pricing`);
                setPricing(updatedPricing);
              } catch (err: unknown) {
                const apiErr = err as { message?: string };
                setError(apiErr.message || 'Failed to set price.');
              } finally {
                setPricingSubmitting(false);
              }
            }}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1rem' }}
          >
            <div style={{ flex: 1, maxWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Price Per Copy ($)</label>
              <input
                type="number"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                min="0"
                step="0.01"
                placeholder="e.g. 25.00"
                required
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
            </div>
            <button
              type="submit"
              disabled={pricingSubmitting || !priceInput}
              style={{ padding: '0.5rem 1rem', backgroundColor: pricingSubmitting ? '#9ca3af' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: pricingSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
            >
              {pricingSubmitting ? 'Setting...' : 'Set Price'}
            </button>
          </form>

          {/* Price History */}
          {pricing.length > 0 && (
            <div>
              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Price History</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {pricing.map((p) => (
                  <div
                    key={p.id}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                  >
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>${Number(p.pricePerCopy).toFixed(2)}</span>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{new Date(p.setAt).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sales & Inventory Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Sales & Inventory</h3>

          {/* Inventory Metrics */}
          <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem', flexWrap: 'wrap', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Total Printed</span>
              <span style={{ fontWeight: 600, fontSize: '1.1rem', color: '#111827' }}>{inventory?.totalPrinted ?? 0}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Total Sold</span>
              <span style={{ fontWeight: 600, fontSize: '1.1rem', color: '#111827' }}>{inventory?.totalSold ?? 0}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Remaining</span>
              <span style={{ fontWeight: 600, fontSize: '1.1rem', color: (inventory?.remaining ?? 0) <= 0 ? '#d32f2f' : '#111827' }}>
                {inventory?.remaining ?? 0}
              </span>
            </div>
            {(inventory?.remaining ?? 0) <= 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#d32f2f', color: '#fff', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                  Sold Out
                </span>
              </div>
            )}
          </div>

          {/* Record Sale Form */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#374151' }}>Record Sale</h4>
            {saleWarning && (
              <div style={{ color: '#92400e', backgroundColor: '#fef3c7', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.8rem', border: '1px solid #fcd34d' }}>
                ⚠️ {saleWarning}
              </div>
            )}
            <form
              onSubmit={async (e: React.FormEvent) => {
                e.preventDefault();
                if (!activeEdition || !saleForm.quantity || !saleForm.paymentMethod) return;
                const qty = parseInt(saleForm.quantity, 10);
                if (isNaN(qty) || qty < 1) return;

                // Check inventory warning
                if (inventory && qty > inventory.remaining) {
                  setSaleWarning(`Warning: Sale quantity (${qty}) exceeds remaining inventory (${inventory.remaining}).`);
                } else {
                  setSaleWarning('');
                }

                setSaleSubmitting(true);
                setError('');
                try {
                  const payload: Record<string, unknown> = {
                    quantity: qty,
                    paymentMethod: saleForm.paymentMethod,
                  };
                  if (saleForm.buyerName.trim()) payload.buyerName = saleForm.buyerName.trim();

                  await apiClient(`/weekend-booklet/editions/${activeEdition.id}/sales`, { method: 'POST', body: payload });
                  setSaleForm({ quantity: '', buyerName: '', paymentMethod: '' });
                  setSaleWarning('');
                  await loadData();
                } catch (err: unknown) {
                  const apiErr = err as { message?: string };
                  setError(apiErr.message || 'Failed to record sale.');
                } finally {
                  setSaleSubmitting(false);
                }
              }}
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}
            >
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Quantity *</label>
                <input
                  type="number"
                  value={saleForm.quantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSaleForm({ ...saleForm, quantity: val });
                    const qty = parseInt(val, 10);
                    if (!isNaN(qty) && inventory && qty > inventory.remaining) {
                      setSaleWarning(`Warning: Sale quantity (${qty}) exceeds remaining inventory (${inventory.remaining}).`);
                    } else {
                      setSaleWarning('');
                    }
                  }}
                  required
                  min="1"
                  placeholder="e.g. 5"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Buyer Name</label>
                <input
                  type="text"
                  value={saleForm.buyerName}
                  onChange={(e) => setSaleForm({ ...saleForm, buyerName: e.target.value })}
                  placeholder="Optional"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#374151' }}>Payment Method *</label>
                <select
                  value={saleForm.paymentMethod}
                  onChange={(e) => setSaleForm({ ...saleForm, paymentMethod: e.target.value as PaymentMethod })}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box', backgroundColor: '#fff' }}
                >
                  <option value="">Select...</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="CreditCard">Credit Card</option>
                  <option value="BankTransfer">Bank Transfer</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Zelle">Zelle</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={saleSubmitting || !saleForm.quantity || !saleForm.paymentMethod}
                style={{ padding: '0.5rem 1.25rem', backgroundColor: saleSubmitting ? '#9ca3af' : '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: saleSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600, height: 'fit-content' }}
              >
                {saleSubmitting ? 'Recording...' : 'Record Sale'}
              </button>
            </form>
          </div>

          {/* Sales List */}
          {sales.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>No sales recorded yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {sales.map((sale) => (
                <div
                  key={sale.id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                >
                  <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Buyer</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{sale.buyerName || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Quantity</span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{sale.quantity}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Payment</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{sale.paymentMethod}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Revenue</span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#16a34a' }}>${Number(sale.revenue).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>Date</span>
                      <span style={{ fontSize: '0.85rem', color: '#374151' }}>{new Date(sale.saleDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderDashboardTab() {
    if (!activeEdition) {
      return (
        <div style={styles.emptyState}>
          <p>No active edition</p>
        </div>
      );
    }

    const deadlineInfo = getDeadlineInfo(activeEdition.submissionDeadline);

    // Determine print order status label and color
    let printStatusLabel = 'Not Ordered';
    let printStatusColor = '#6b7280';
    if (progress) {
      if (progress.printOrderStatus === 'delivered') {
        printStatusLabel = 'Delivered';
        printStatusColor = '#16a34a';
      } else if (progress.printOrderStatus === 'ordered') {
        printStatusLabel = 'Ordered';
        printStatusColor = '#2563eb';
      }
    }

    return (
      <div>
        {/* Production Progress Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Production Progress</h3>

          {/* Status Counts */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
              Submissions by Status
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {progress?.statusCounts && progress.statusCounts.length > 0 ? (
                progress.statusCounts.map((sc) => (
                  <div
                    key={sc.status}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.4rem 0.75rem',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_BADGE_COLORS[sc.status] || '#6b7280',
                      }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#333' }}>
                      {sc.status.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: STATUS_BADGE_COLORS[sc.status] || '#6b7280' }}>
                      {sc.count}
                    </span>
                  </div>
                ))
              ) : (
                <span style={{ fontSize: '0.85rem', color: '#999' }}>No submissions yet</span>
              )}
            </div>
          </div>

          {/* Total Submissions & Deadline & Print Status */}
          <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Submissions</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f2d1f' }}>
                {progress?.totalSubmissions ?? 0}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deadline</span>
              <span style={{ fontSize: '1rem', fontWeight: 600, color: deadlineInfo.color }}>
                {deadlineInfo.text}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Print Status</span>
              <span style={{ fontSize: '1rem', fontWeight: 600, color: printStatusColor }}>
                {printStatusLabel}
              </span>
            </div>
          </div>

          {/* Contributors */}
          {activeEdition && <ContributorsSection editionId={activeEdition.id} />}
        </div>

        {/* Revenue Cards — 3-column grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Booklet Sales Card */}
          <div style={{ padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
              Booklet Sales
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' }}>
              ${revenueSummary?.totalRevenue?.toFixed(2) ?? '0.00'}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {revenueSummary?.transactionCount ?? 0} transaction{(revenueSummary?.transactionCount ?? 0) !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Booklet Spaces Card */}
          <div style={{ padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
              Booklet Spaces
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' }}>
              ${spaceRevenueSummary?.grandTotal?.toFixed(2) ?? '0.00'}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {(spaceRevenueSummary?.advertisers?.count ?? 0) + (spaceRevenueSummary?.tileSpacePurchasers?.count ?? 0)} purchaser{((spaceRevenueSummary?.advertisers?.count ?? 0) + (spaceRevenueSummary?.tileSpacePurchasers?.count ?? 0)) !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Sponsors Card */}
          <div style={{ padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
              Sponsors
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' }}>
              {submissions.filter((s) => s.sectionCategory === 'SponsorAds').length}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              sponsor ad{submissions.filter((s) => s.sectionCategory === 'SponsorAds').length !== 1 ? 's' : ''} submitted
            </div>
          </div>
        </div>

        {/* Revenue Summary Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Revenue Summary</h3>

          {!revenueSummary || revenueSummary.totalRevenue === 0 ? (
            <div style={{ textAlign: 'center' as const, padding: '1.5rem', color: '#666' }}>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>No sales recorded yet</p>
            </div>
          ) : (
            <div>
              {/* Total Revenue & Transaction Count */}
              <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Revenue</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c' }}>
                    ${revenueSummary.totalRevenue.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Transactions</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f2d1f' }}>
                    {revenueSummary.transactionCount}
                  </span>
                </div>
              </div>

              {/* Breakdown by Payment Method */}
              <div>
                <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                  Breakdown by Payment Method
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 600 }}>Method</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 600 }}>Revenue</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 600 }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueSummary.breakdown.map((item) => (
                      <tr key={item.paymentMethod} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#333' }}>
                          {item.paymentMethod.replace(/([A-Z])/g, ' $1').trim()}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#1a6b3c', fontWeight: 600 }}>
                          ${item.total.toFixed(2)}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#666' }}>
                          {item.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Space Revenue Summary Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Space Revenue</h3>

          {!spaceRevenueSummary || spaceRevenueSummary.grandTotal === 0 ? (
            <div style={{ textAlign: 'center' as const, padding: '1.5rem', color: '#666' }}>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>No space revenue recorded yet</p>
            </div>
          ) : (
            <div>
              {/* Grand Total */}
              <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Grand Total</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c' }}>
                    ${spaceRevenueSummary.grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Category Breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {/* Advertisers */}
                <div style={{ padding: '1rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                    Advertisers
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' }}>
                    ${spaceRevenueSummary.advertisers.totalRevenue.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {spaceRevenueSummary.advertisers.count} {spaceRevenueSummary.advertisers.count === 1 ? 'entry' : 'entries'}
                  </div>
                </div>

                {/* Tile Space Purchasers */}
                <div style={{ padding: '1rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                    Tile Space Purchasers
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' }}>
                    ${spaceRevenueSummary.tileSpacePurchasers.totalRevenue.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {spaceRevenueSummary.tileSpacePurchasers.count} {spaceRevenueSummary.tileSpacePurchasers.count === 1 ? 'entry' : 'entries'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Inscription Options panel is on the Submissions tab */}
      </div>
    );
  }

  // ── Tile Card Renderer ────────────────────────────────────────

  function renderTileCard(tile: BookletSubmission): JSX.Element {
    const colors = STATUS_COLORS[tile.status];
    return (
      <div key={tile.id} style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1f2937', lineHeight: 1.3, marginBottom: '0.35rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tile.title}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.35rem' }}>
          {tile.authorName}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 600, backgroundColor: colors.bg, color: colors.text, whiteSpace: 'nowrap' }}>
            {tile.status}
          </span>
          {tile.spaceRevenue && (
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#16a34a' }}>
              ${tile.spaceRevenue.amount.toFixed(2)}
            </span>
          )}
        </div>
        {tile.referenceId && (
          <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.1rem' }}>
            Ref: {tile.referenceId}
          </div>
        )}
      </div>
    );
  }

  // ── Article Detail Modal ──────────────────────────────────────

  function ArticleDetailModal({ article, onClose }: { article: BookletSubmission; onClose: () => void }) {
    const statusColors = STATUS_COLORS[article.status];
    const wordCount = article.body ? article.body.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length : 0;
    const formattedDate = new Date(article.createdAt).toLocaleDateString();

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onClose]);

    return (
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '85vh',
            overflowY: 'auto',
            padding: '2rem',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}
        >
          {/* Title */}
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: '0 0 1rem 0' }}>
            {article.title}
          </h2>

          {/* Author */}
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Author</span>
            <div style={{ fontSize: '0.95rem', color: '#374151', marginTop: '0.15rem' }}>{article.authorName}</div>
          </div>

          {/* Status Badge */}
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</span>
            <div style={{ marginTop: '0.15rem' }}>
              <span style={{
                display: 'inline-block',
                padding: '0.2rem 0.6rem',
                borderRadius: '9999px',
                fontSize: '0.8rem',
                fontWeight: 600,
                backgroundColor: statusColors.bg,
                color: statusColors.text,
              }}>
                {article.status}
              </span>
            </div>
          </div>

          {/* Booklet Section */}
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Booklet Section</span>
            <div style={{ fontSize: '0.95rem', color: '#374151', marginTop: '0.15rem' }}>
              {article.bookletSection || 'Unassigned'}
            </div>
          </div>

          {/* Reference ID */}
          {article.referenceId && (
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Reference ID</span>
              <div style={{ fontSize: '0.95rem', color: '#374151', marginTop: '0.15rem' }}>
                {article.referenceId}
              </div>
            </div>
          )}

          {/* Body Content */}
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Content</span>
            {article.body ? (
              <div
                dangerouslySetInnerHTML={{ __html: article.body }}
                style={{
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#f9fafb',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginTop: '0.25rem',
                  color: '#374151',
                }}
              />
            ) : (
              <div style={{ fontStyle: 'italic', color: '#9ca3af', marginTop: '0.25rem' }}>
                No content submitted yet
              </div>
            )}
          </div>

          {/* Word Count */}
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Word Count</span>
            <div style={{ fontSize: '0.95rem', color: '#374151', marginTop: '0.15rem' }}>{wordCount}</div>
          </div>

          {/* Attachment Download */}
          {article.attachmentUrl && (
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Attachment</span>
              <div style={{ marginTop: '0.25rem' }}>
                <a
                  href={`${import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000')}/public/uploads/${article.attachmentUrl.split('/').pop()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', color: '#4f46e5', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}
                >
                  📄 {article.attachmentUrl.split('/').pop()}
                  <span style={{ fontSize: '0.75rem', color: '#6366f1', marginLeft: '0.25rem' }}>↗ View / Download</span>
                </a>
              </div>
            </div>
          )}

          {/* Submitted Date */}
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Submitted</span>
            <div style={{ fontSize: '0.95rem', color: '#374151', marginTop: '0.15rem' }}>{formattedDate}</div>
          </div>

          {/* Reviewers */}
          {article.reviewers && article.reviewers.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Reviewers</span>
              <div style={{ marginTop: '0.25rem' }}>
                {article.reviewers.map((r) => (
                  <div key={r.id} style={{ fontSize: '0.9rem', color: '#374151' }}>
                    {r.member ? `${r.member.firstName} ${r.member.lastName}` : r.memberId}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          {article.comments && article.comments.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>Comments</span>
              <div style={{ marginTop: '0.25rem' }}>
                {article.comments.map((c) => (
                  <div key={c.id} style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <span style={{ fontWeight: 600 }}>{c.commenterName}:</span> {c.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Article Card Renderer ──────────────────────────────────────

  function renderArticleCard(article: BookletSubmission) {
    const colors = STATUS_COLORS[article.status];
    const sectionColor = COLUMN_COLORS[article.bookletSection || 'Unassigned']?.headerBg || '#6b7280';
    return (
      <div key={article.id} onClick={() => setSelectedArticle(article)} style={{ padding: '0.6rem 0.75rem', marginBottom: '0.4rem', backgroundColor: '#fff', borderLeft: `3px solid ${sectionColor}`, borderRadius: '5px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer' }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1f2937', lineHeight: 1.4, marginBottom: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {article.title}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
            {article.authorName}
          </span>
          <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.68rem', fontWeight: 600, backgroundColor: colors.bg, color: colors.text, whiteSpace: 'nowrap' }}>
            {article.status}
          </span>
        </div>
        {article.referenceId && (
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.15rem' }}>
            Ref: {article.referenceId}
          </div>
        )}
        {article.attachmentUrl && (
          <div style={{ fontSize: '0.7rem', color: '#4f46e5', marginTop: '0.15rem' }}>📎 File attached</div>
        )}
      </div>
    );
  }

  // ── Articles Tab ─────────────────────────────────────────────

  function renderArticlesTab() {
    // Loading state
    if (loading) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.loading}>Loading articles...</div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.error}>{error}</div>
        </div>
      );
    }

    // No active edition
    if (!activeEdition) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.emptyState}>No active edition available</div>
        </div>
      );
    }

    // Filter for GeneralArticles only
    const generalArticles = submissions.filter(s => s.sectionCategory === 'GeneralArticles');

    // No articles found
    if (generalArticles.length === 0) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.emptyState}>No articles found</div>
        </div>
      );
    }

    // Group by bookletSection for Kanban view
    const grouped = groupBySection(generalArticles);

    // Column order: Unassigned first, then the eight booklet sections
    const columnOrder: string[] = ['Unassigned', ...BOOKLET_SECTIONS];

    // View toggle button styles
    const toggleBaseStyle: React.CSSProperties = {
      padding: '0.5rem 1rem',
      fontSize: '0.85rem',
      fontWeight: 600,
      border: '1px solid #e0e0e0',
      cursor: 'pointer',
      transition: 'background-color 0.15s, color 0.15s',
    };
    const toggleActiveStyle: React.CSSProperties = {
      ...toggleBaseStyle,
      backgroundColor: '#1a6b3c',
      color: '#fff',
      borderColor: '#1a6b3c',
    };
    const toggleInactiveStyle: React.CSSProperties = {
      ...toggleBaseStyle,
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderColor: '#e0e0e0',
    };

    return (
      <div style={styles.tabContent}>
        {/* View Toggle */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '1rem' }}>
          <button
            style={{ ...articlesViewMode === 'kanban' ? toggleActiveStyle : toggleInactiveStyle, borderRadius: '6px 0 0 6px' }}
            onClick={() => setArticlesViewMode('kanban')}
          >
            Kanban
          </button>
          <button
            style={{ ...articlesViewMode === 'table' ? toggleActiveStyle : toggleInactiveStyle, borderRadius: '0 6px 6px 0', borderLeft: 'none' }}
            onClick={() => setArticlesViewMode('table')}
          >
            Table
          </button>
        </div>

        {/* Conditional content based on view mode */}
        {articlesViewMode === 'kanban' ? (
          <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', padding: '0.25rem 0' }}>
            {columnOrder.map(section => {
              const colColors = COLUMN_COLORS[section] || COLUMN_COLORS['Unassigned'];
              return (
              <div key={section} style={{ minWidth: '200px', maxWidth: '230px', flex: '0 0 auto', borderRadius: '6px', border: `1px solid ${colColors.border}`, overflow: 'hidden', backgroundColor: colColors.bodyBg }}>
                {/* Column header */}
                <div style={{ fontWeight: 600, padding: '0.4rem 0.5rem', fontSize: '0.7rem', color: colColors.headerText, backgroundColor: colColors.headerBg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section}</span>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '9999px', padding: '0.05rem 0.4rem', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0, marginLeft: '0.25rem' }}>{grouped[section]?.length || 0}</span>
                </div>
                {/* Cards container with vertical scroll */}
                <div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '0.3rem' }}>
                  {(grouped[section] || []).map(article => renderArticleCard(article))}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>Title</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>Author</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>Booklet Section</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>Ref ID</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>Reviewers</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>File</th>
              </tr>
            </thead>
            <tbody>
              {generalArticles.map(article => {
                const colors = STATUS_COLORS[article.status];
                return (
                  <tr key={article.id} style={{ borderBottom: '1px solid #e5e7eb' }} onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '#f9fafb'; }} onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = ''; }}>
                    <td style={{ padding: '0.75rem', color: '#1f2937', fontWeight: 500 }}>{article.title}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{article.authorName}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{article.bookletSection || 'Unassigned'}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{article.referenceId || ''}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: colors.bg, color: colors.text }}>
                        {article.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.8rem' }}>
                      {(article as any).reviewers && (article as any).reviewers.length > 0
                        ? (article as any).reviewers.map((r: any) => `${r.member.firstName} ${r.member.lastName}`).join(', ')
                        : '—'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {article.attachmentUrl ? (
                        <a
                          href={`${import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000')}/public/uploads/${article.attachmentUrl.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#4f46e5', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}
                          title={article.attachmentUrl.split('/').pop()}
                        >
                          📄 Download
                        </a>
                      ) : (
                        <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  function renderMemorialTilesTab() {
    if (loading) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.loading}>Loading memorial tile submissions...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.error}>{error}</div>
        </div>
      );
    }

    const tiles = submissions.filter(s => s.sectionCategory === 'TileSpacePurchasers');

    if (tiles.length === 0) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.emptyState}>No memorial tile submissions found</div>
        </div>
      );
    }

    // Kanban columns by status
    const TILE_STATUS_COLUMNS: SubmissionStatus[] = ['Draft', 'Submitted', 'UnderReview', 'Approved', 'ReadyForLayout'];
    const TILE_STATUS_LABELS: Record<string, string> = {
      Draft: 'Draft',
      Submitted: 'Submitted',
      UnderReview: 'Under Review',
      RevisionRequested: 'Revision',
      Approved: 'Approved',
      ReadyForLayout: 'Ready for Layout',
    };
    const TILE_STATUS_COLORS: Record<string, { headerBg: string; headerText: string; bodyBg: string; border: string }> = {
      Draft: { headerBg: '#6b7280', headerText: '#fff', bodyBg: '#f9fafb', border: '#e5e7eb' },
      Submitted: { headerBg: '#2563eb', headerText: '#fff', bodyBg: '#eff6ff', border: '#bfdbfe' },
      UnderReview: { headerBg: '#d97706', headerText: '#fff', bodyBg: '#fffbeb', border: '#fde68a' },
      Approved: { headerBg: '#16a34a', headerText: '#fff', bodyBg: '#f0fdf4', border: '#bbf7d0' },
      ReadyForLayout: { headerBg: '#7c3aed', headerText: '#fff', bodyBg: '#faf5ff', border: '#ddd6fe' },
    };

    // Group tiles by status
    const grouped: Record<string, BookletSubmission[]> = {};
    for (const col of TILE_STATUS_COLUMNS) {
      grouped[col] = [];
    }
    for (const tile of tiles) {
      if (grouped[tile.status]) {
        grouped[tile.status].push(tile);
      } else {
        // Put RevisionRequested into Draft column or create its own
        if (!grouped[tile.status]) grouped[tile.status] = [];
        grouped[tile.status].push(tile);
      }
    }

    return (
      <div style={styles.tabContent}>
        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', padding: '0.25rem 0' }}>
          {TILE_STATUS_COLUMNS.map(column => {
            const colColors = TILE_STATUS_COLORS[column] || TILE_STATUS_COLORS['Draft'];
            return (
              <div
                key={column}
                style={{ minWidth: '220px', maxWidth: '260px', flex: '0 0 auto', borderRadius: '6px', border: `1px solid ${colColors.border}`, backgroundColor: colColors.bodyBg }}
              >
                {/* Column header */}
                <div style={{ fontWeight: 600, padding: '0.4rem 0.5rem', fontSize: '0.7rem', color: colColors.headerText, backgroundColor: colColors.headerBg, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '6px 6px 0 0' }}>
                  <span>{TILE_STATUS_LABELS[column] || column}</span>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '9999px', padding: '0.05rem 0.4rem', fontSize: '0.6rem', fontWeight: 700 }}>{grouped[column]?.length || 0}</span>
                </div>
                {/* Cards */}
                <div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '0.3rem', minHeight: '60px' }}>
                  {(grouped[column] || []).map(tile => renderTileKanbanCard(tile, TILE_STATUS_COLUMNS))}
                  {(grouped[column] || []).length === 0 && (
                    <div style={{ padding: '1rem 0.5rem', textAlign: 'center', fontSize: '0.7rem', color: '#9ca3af', fontStyle: 'italic' }}>
                      No tiles
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderTileKanbanCard(tile: BookletSubmission, statusColumns: SubmissionStatus[]): JSX.Element {
    const colors = STATUS_COLORS[tile.status];
    return (
      <div key={tile.id} style={{ padding: '0.4rem 0.5rem', marginBottom: '0.3rem', backgroundColor: '#fff', borderLeft: `3px solid ${colors.bg}`, borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
        <div style={{ fontWeight: 600, fontSize: '0.75rem', color: '#1f2937', lineHeight: 1.3, marginBottom: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tile.title}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.68rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
            {tile.authorName}
          </span>
          {tile.spaceRevenue && (
            <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#16a34a' }}>
              ${tile.spaceRevenue.amount.toFixed(2)}
            </span>
          )}
        </div>
        {tile.referenceId && (
          <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.1rem' }}>
            Ref: {tile.referenceId}
          </div>
        )}
        {/* Status change dropdown */}
        <select
          value={tile.status}
          onChange={(e) => handleTileStatusChange(tile.id, e.target.value as SubmissionStatus)}
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: '0.25rem', width: '100%', padding: '0.2rem 0.3rem', fontSize: '0.65rem', border: '1px solid #e5e7eb', borderRadius: '3px', backgroundColor: '#f9fafb', color: '#374151', cursor: 'pointer' }}
          aria-label={`Change status for ${tile.title}`}
        >
          {statusColumns.map(s => (
            <option key={s} value={s}>{s === 'UnderReview' ? 'Under Review' : s === 'ReadyForLayout' ? 'Ready for Layout' : s}</option>
          ))}
        </select>
      </div>
    );
  }

  async function handleTileStatusChange(submissionId: string, newStatus: SubmissionStatus) {
    // Optimistic update
    setSubmissions(prev => prev.map(s =>
      s.id === submissionId ? { ...s, status: newStatus } : s
    ));

    try {
      await apiClient(`/weekend-booklet/submissions/${submissionId}`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
    } catch {
      // Rollback on failure
      await loadData();
    }
  }

  // ── Ad Card Renderer ─────────────────────────────────────────

  function renderAdCard(ad: BookletSubmission): JSX.Element {
    const colors = STATUS_COLORS[ad.status];
    const borderColor = AD_COLUMN_COLORS[ad.adSize || 'Unassigned']?.headerBg || '#6b7280';
    return (
      <div
        key={ad.id}
        style={{ padding: '0.4rem 0.5rem', marginBottom: '0.3rem', backgroundColor: '#fff', borderLeft: `3px solid ${borderColor}`, borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}
      >
        <div style={{ fontWeight: 600, fontSize: '0.75rem', color: '#1f2937', lineHeight: 1.3, marginBottom: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ad.title}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.68rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
            {ad.authorName}
          </span>
          <span style={{ display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: 600, backgroundColor: colors.bg, color: colors.text, whiteSpace: 'nowrap' }}>
            {ad.status}
          </span>
        </div>
        {ad.referenceId && (
          <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.1rem' }}>
            Ref: {ad.referenceId}
          </div>
        )}
        {/* Inline ad size selector */}
        <select
          value={ad.adSize || ''}
          onChange={(e) => handleAdSizeChange(ad.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: '0.25rem', width: '100%', padding: '0.2rem 0.3rem', fontSize: '0.65rem', border: '1px solid #e5e7eb', borderRadius: '3px', backgroundColor: '#f9fafb', color: '#374151', cursor: 'pointer' }}
          aria-label={`Change ad size for ${ad.title}`}
        >
          <option value="">Unassigned</option>
          <option value="FullPage">Full Page</option>
          <option value="HalfPage">Half Page</option>
          <option value="QuarterPage">Quarter Page</option>
        </select>
      </div>
    );
  }

  async function handleAdSizeChange(submissionId: string, newAdSize: string) {
    const adSizeValue = newAdSize || null;

    // Optimistic update
    setSubmissions(prev => prev.map(s =>
      s.id === submissionId ? { ...s, adSize: adSizeValue as AdSize | null } : s
    ));

    try {
      await apiClient(`/weekend-booklet/submissions/${submissionId}`, {
        method: 'PATCH',
        body: { adSize: adSizeValue },
      });
    } catch {
      // Rollback on failure
      await loadData();
    }
  }

  function renderAdSpaceTab() {
    // Loading state
    if (loading) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.loading}>Loading ad space submissions...</div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.error}>{error}</div>
        </div>
      );
    }

    // Filter for Advertisers only
    const ads = submissions.filter(s => s.sectionCategory === 'Advertisers');

    // Empty state
    if (ads.length === 0) {
      return (
        <div style={styles.tabContent}>
          <div style={styles.emptyState}>No ad space submissions found</div>
        </div>
      );
    }

    // Group by ad size for kanban columns
    const grouped = groupByAdSize(ads);

    return (
      <div style={styles.tabContent}>
        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', padding: '0.25rem 0' }}>
          {AD_SPACE_COLUMNS.map(column => {
            const colColors = AD_COLUMN_COLORS[column] || AD_COLUMN_COLORS['Unassigned'];
            return (
              <div
                key={column}
                style={{ minWidth: '220px', maxWidth: '260px', flex: '0 0 auto', borderRadius: '6px', border: `1px solid ${colColors.border}`, backgroundColor: colColors.bodyBg }}
              >
                {/* Column header */}
                <div style={{ fontWeight: 600, padding: '0.4rem 0.5rem', fontSize: '0.7rem', color: colColors.headerText, backgroundColor: colColors.headerBg, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '6px 6px 0 0' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{AD_SIZE_LABELS[column]}</span>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '9999px', padding: '0.05rem 0.4rem', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0, marginLeft: '0.25rem' }}>{grouped[column]?.length || 0}</span>
                </div>
                {/* Cards container with vertical scroll */}
                <div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '0.3rem', minHeight: '60px' }}>
                  {(grouped[column] || []).map(ad => renderAdCard(ad))}
                  {(grouped[column] || []).length === 0 && (
                    <div style={{ padding: '1rem 0.5rem', textAlign: 'center', fontSize: '0.7rem', color: '#9ca3af', fontStyle: 'italic' }}>
                      No ads
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderTemplatesTab() {
    if (loading) {
      return <div style={styles.tabContent}><div style={styles.loading}>Loading templates...</div></div>;
    }
    if (error) {
      return <div style={styles.tabContent}><div style={styles.error}>{error}</div></div>;
    }

    const templates = submissions.filter(s => s.sectionCategory === 'Templates');

    if (templates.length === 0) {
      return <div style={styles.tabContent}><div style={styles.emptyState}>No template submissions found. Create a submission with Section Category "Booklet Templates" to see it here.</div></div>;
    }

    const apiBase = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000');

    function handleTemplateDownload(template: BookletSubmission) {
      if (!template.attachmentUrl) return;
      const filename = template.attachmentUrl.split('/').pop();
      const downloadUrl = `${apiBase}/public/uploads/${filename}`;
      window.open(downloadUrl, '_blank');
    }

    return (
      <div style={styles.tabContent}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {templates.map(template => {
            const statusColor = STATUS_COLORS[template.status];
            const hasAttachment = !!template.attachmentUrl;
            const filename = template.attachmentUrl?.split('/').pop() || '';
            return (
              <div
                key={template.id}
                style={{ padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fff', borderLeft: '4px solid #4f46e5', cursor: hasAttachment ? 'pointer' : 'default' }}
                onClick={() => hasAttachment ? handleTemplateDownload(template) : setSelectedSubmission(template)}
                title={hasAttachment ? `Click to download: ${filename}` : 'No attachment'}
              >
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937', marginBottom: '0.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {template.title}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{template.authorName}</span>
                  <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: statusColor.bg, color: statusColor.text }}>{template.status}</span>
                </div>
                {template.referenceId && (
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>Ref: {template.referenceId}</div>
                )}
                {hasAttachment ? (
                  <div style={{ fontSize: '0.8rem', color: '#4f46e5', marginTop: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    ⬇️ Download Template
                  </div>
                ) : (
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem', fontStyle: 'italic' }}>No file attached</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderTabContent() {
    switch (activeTab) {
      case 'submissions': return renderSubmissionsTab();
      case 'articles': return renderArticlesTab();
      case 'memorialTiles': return renderMemorialTilesTab();
      case 'adSpace': return renderAdSpaceTab();
      case 'templates': return renderTemplatesTab();
      case 'layout': return renderLayoutTab();
      case 'production': return <ProductionLayoutTab canEdit={canEdit} />;
      case 'vendorAccess': return <VendorAccessTab />;
      case 'printSales': return renderPrintSalesTab();
      case 'dashboard': return renderDashboardTab();
      case 'editorial': return <EditorialTab />;
      case 'reviews': return <ReviewCommentsView />;
    }
  }

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h1 style={{ ...styles.title, margin: 0 }}>Weekend Booklet</h1>
          <button
            onClick={() => navigate('/weekend-booklet/faq')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#1a6b3c',
              backgroundColor: '#eef6f0',
              border: '1px solid #d0e8d6',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            ❓ FAQ
          </button>
        </div>
        <div style={styles.loading}>Loading booklet data...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{ ...styles.title, margin: 0 }}>Weekend Booklet</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={async () => {
              if (!confirm('Send a reminder notification to all reviewers with pending reviews?')) return;
              try {
                const result = await apiClient<{ notified: number }>('/weekend-booklet/notify-all-reviewers', { method: 'POST' });
                alert(`Notified ${result.notified} reviewer(s) with pending reviews.`);
              } catch (err: any) {
                alert(err?.message || 'Failed to notify reviewers.');
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#1565c0',
              backgroundColor: '#e3f2fd',
              border: '1px solid #bbdefb',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            🔔 Notify Reviewers
          </button>
          {canEdit && (
          <button
            onClick={() => setShowNotifyModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#6a1b9a',
              backgroundColor: '#f3e5f5',
              border: '1px solid #ce93d8',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            📢 Notify All Reviewers
          </button>
          )}
          <button
            onClick={() => navigate('/weekend-booklet/faq')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#1a6b3c',
              backgroundColor: '#eef6f0',
              border: '1px solid #d0e8d6',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            ❓ FAQ
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        {TABS.filter((tab) => {
          if (tab.key === 'editorial') return canEditContent || committeeMembers.some((m) => m.userId === authUser?.sub);
          if (tab.key === 'reviews') return canEditContent || authUser?.role === 'Delegate';
          return true;
        }).map((tab) => (
          <button
            key={tab.key}
            style={activeTab === tab.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {renderTabContent()}
      </div>

      {selectedArticle && (
        <ArticleDetailModal
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
        />
      )}

      {/* Review Modal */}
      {reviewModalOpen && reviewModalSubmission && (
        <ReviewModal
          submission={reviewModalSubmission}
          assignment={reviewModalAssignment}
          onClose={handleReviewModalClose}
          onCommentAdded={handleReviewCommentAdded}
          onStatusChanged={handleReviewStatusChanged}
        />
      )}

      {/* Notify All Reviewers Modal */}
      {showNotifyModal && (
        <div
          onClick={() => { if (!notifySending) { setShowNotifyModal(false); setNotifySubject(''); setNotifyMessage(''); setNotifySuccess(false); } }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              padding: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', margin: '0 0 1.25rem 0' }}>
              📢 Send Note to All Reviewers
            </h2>

            {notifySuccess ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#16a34a', fontSize: '1.1rem', fontWeight: 600 }}>
                ✓ Notification sent to all reviewers
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>Subject</label>
                  <input
                    type="text"
                    value={notifySubject}
                    onChange={(e) => setNotifySubject(e.target.value)}
                    placeholder="Subject line..."
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.9rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>Message</label>
                  <textarea
                    value={notifyMessage}
                    onChange={(e) => setNotifyMessage(e.target.value)}
                    placeholder="Enter your message..."
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.9rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowNotifyModal(false); setNotifySubject(''); setNotifyMessage(''); }}
                    disabled={notifySending}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: '#374151',
                      backgroundColor: '#e5e7eb',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: notifySending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!notifySubject.trim() || !notifyMessage.trim()) return;
                      setNotifySending(true);
                      try {
                        const announcement = await apiClient<{ id: string }>('/announcements', {
                          method: 'POST',
                          body: { title: `📋 Reviewer Note: ${notifySubject.trim()}`, body: notifyMessage.trim(), audience: 'DelegatesOnly' },
                        });
                        await apiClient(`/announcements/${announcement.id}/publish`, { method: 'POST' });
                        setNotifySuccess(true);
                        setTimeout(() => {
                          setShowNotifyModal(false);
                          setNotifySubject('');
                          setNotifyMessage('');
                          setNotifySuccess(false);
                          setNotifySending(false);
                        }, 1500);
                      } catch (err: any) {
                        alert(err?.message || 'Failed to send notification.');
                        setNotifySending(false);
                      }
                    }}
                    disabled={notifySending || !notifySubject.trim() || !notifyMessage.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: notifySending || !notifySubject.trim() || !notifyMessage.trim() ? '#9ca3af' : '#1a6b3c',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: notifySending || !notifySubject.trim() || !notifyMessage.trim() ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {notifySending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
